// screens/admin/TeamTournamentTester.js
// ============================================================================
// TEAM TOURNAMENT END-TO-END TESTER
// Tests the complete flow using ACTUAL backend functions:
// Create -> Entries -> Lock -> Seed -> Matches -> Advancement -> Payouts
// ============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebaseConfig';
import {
  doc, getDoc, getDocs, collection, query, where, orderBy,
  setDoc, updateDoc, deleteDoc, writeBatch, serverTimestamp,
  Timestamp, onSnapshot, increment,
} from 'firebase/firestore';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  purple: '#a855f7',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

const TEAM_NAMES = [
  'Alpha', 'Bravo', 'Charlie', 'Delta', 'Echo', 'Foxtrot', 'Golf', 'Hotel', 
  'India', 'Juliet', 'Kilo', 'Lima', 'Mike', 'November', 'Oscar', 'Papa',
  'Quebec', 'Romeo', 'Sierra', 'Tango', 'Uniform', 'Victor', 'Whiskey', 'X-Ray',
  'Yankee', 'Zulu', 'Phoenix', 'Dragon', 'Titan', 'Storm', 'Shadow', 'Thunder',
];
const PLAYER_NAMES = ['Ace', 'Blaze', 'Crypto', 'Drift', 'Echo', 'Frost', 'Ghost', 'Hawk', 'Iron', 'Jinx', 'Karma', 'Luna', 'Maverick', 'Neon', 'Omega', 'Phoenix', 'Quantum', 'Raven', 'Storm', 'Titan', 'Ultra', 'Viper', 'Wolf', 'Xero', 'Yuki', 'Zero'];
const VIEWER_NAMES = ['StreamFan', 'HypeTrain', 'BigDonor', 'WhaleFlipper', 'TsunamiKing', 'WaveRider', 'RippleMaker', 'PoolFlooder', 'CashSplash', 'MoneyWave', 'TipMaster', 'PrizeBooster', 'Supporter', 'Champion', 'Legend'];

const getRandomName = () => PLAYER_NAMES[Math.floor(Math.random() * PLAYER_NAMES.length)] + Math.floor(Math.random() * 999);
const getTeamName = (index) => {
  const name = TEAM_NAMES[index % TEAM_NAMES.length];
  const suffix = index >= TEAM_NAMES.length ? ` ${Math.floor(index / TEAM_NAMES.length) + 1}` : '';
  return `Team ${name}${suffix}`;
};
const getViewerName = (index) => {
  const name = VIEWER_NAMES[index % VIEWER_NAMES.length];
  const suffix = Math.floor(Math.random() * 9999);
  return `${name}${suffix}`;
};

// ============================================================================
// FLOOD THE POOL SIMULATION CONFIG
// ============================================================================

// Mirrors backend constants from floodThePool.js
const FLOOD_CONFIG = {
  // Contribution limits (cents)
  MIN_CONTRIBUTION: 100,      // $1
  MAX_CONTRIBUTION: 100000,   // $1,000
  
  // Platform fee
  PLATFORM_FEE_PERCENT: 10,
  
  // Tsunami milestones (cents)
  MILESTONES: [
    100000,    // $1,000
    250000,    // $2,500
    500000,    // $5,000
    750000,    // $7,500
    1000000,   // $10,000
    2500000,   // $25,000
    5000000,   // $50,000
    10000000,  // $100,000
  ],
  
  // Simulation settings
  VIEWERS_PER_MATCH: 5,           // How many viewers contribute per match
  CONTRIBUTION_CHANCE: 0.7,       // 70% chance a viewer contributes each match
  
  // Contribution amount distribution (weighted)
  CONTRIBUTION_WEIGHTS: [
    { min: 100, max: 500, weight: 50 },      // $1-$5: 50% chance (ripples)
    { min: 500, max: 1000, weight: 25 },     // $5-$10: 25% chance (small waves)
    { min: 1000, max: 5000, weight: 15 },    // $10-$50: 15% chance (waves)
    { min: 5000, max: 10000, weight: 7 },    // $50-$100: 7% chance (surges)
    { min: 10000, max: 50000, weight: 3 },   // $100-$500: 3% chance (tsunamis!)
  ],
};

// Wave type classification (matches backend)
const getWaveType = (amountCents) => {
  if (amountCents >= 10000) return { type: 'tsunami', icon: '~~~~', name: 'TSUNAMI', color: '#FCD000' };
  if (amountCents >= 5000)  return { type: 'surge', icon: '~~~', name: 'Surge', color: '#00bfff' };
  if (amountCents >= 1000)  return { type: 'wave', icon: '~~', name: 'Wave', color: '#4ade80' };
  return { type: 'ripple', icon: '~', name: 'Ripple', color: '#888' };
};

// Generate random contribution amount based on weights
const getRandomContributionAmount = () => {
  const totalWeight = FLOOD_CONFIG.CONTRIBUTION_WEIGHTS.reduce((sum, w) => sum + w.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const tier of FLOOD_CONFIG.CONTRIBUTION_WEIGHTS) {
    random -= tier.weight;
    if (random <= 0) {
      return Math.floor(tier.min + Math.random() * (tier.max - tier.min));
    }
  }
  
  // Fallback to minimum
  return FLOOD_CONFIG.MIN_CONTRIBUTION;
};

// Test scenarios configuration
const SCENARIOS = {
  // === SMALL BRACKETS (4 teams) ===
  'perfect_4': { 
    label: '4 Full Teams (Perfect bracket)', 
    fullTeams: 4, partials: [], solos: 0,
    expectedTeams: 4, expectedBracket: 4, expectedTapered: 0,
    forfeitChance: 0, // No forfeits
  },
  'mixed_small': { 
    label: 'Mixed: 2 full + partials + solos', 
    fullTeams: 2, partials: [4, 3, 2], solos: 7,
    expectedTeams: 5, expectedBracket: 4, expectedTapered: 1,
    forfeitChance: 0,
  },
  'all_solos': { 
    label: 'All Solos (24 players)', 
    fullTeams: 0, partials: [], solos: 24,
    expectedTeams: 4, expectedBracket: 4, expectedTapered: 0,
    forfeitChance: 0,
  },
  'all_partials': { 
    label: 'All Partials (mixed sizes)', 
    fullTeams: 0, partials: [4, 4, 3, 3, 2, 2, 2, 2, 2], solos: 0,
    expectedTeams: 4, expectedBracket: 4, expectedTapered: 0,
    forfeitChance: 0,
  },
  
  // === MEDIUM BRACKETS (8 teams) ===
  'perfect_8': { 
    label: '8 Full Teams (Perfect bracket)', 
    fullTeams: 8, partials: [], solos: 0,
    expectedTeams: 8, expectedBracket: 8, expectedTapered: 0,
    forfeitChance: 0,
  },
  'taper_to_8': { 
    label: 'Taper to 8 (10 teams formed)', 
    fullTeams: 6, partials: [4, 3, 2], solos: 9,
    expectedTeams: 10, expectedBracket: 8, expectedTapered: 2,
    forfeitChance: 0,
  },
  'perfect_8_forfeits': { 
    label: '8 Teams + Forfeits (25%)', 
    fullTeams: 8, partials: [], solos: 0,
    expectedTeams: 8, expectedBracket: 8, expectedTapered: 0,
    forfeitChance: 0.25, // 25% of matches have a forfeit
    forfeitType: 'single', // One team forfeits (other wins)
  },
  
  // === LARGE BRACKETS (16 teams) ===
  'perfect_16': { 
    label: '16 Full Teams (4 rounds)', 
    fullTeams: 16, partials: [], solos: 0,
    expectedTeams: 16, expectedBracket: 16, expectedTapered: 0,
    forfeitChance: 0,
  },
  'taper_to_16': { 
    label: 'Taper to 16 (20 teams formed)', 
    fullTeams: 12, partials: [4, 4, 3, 3, 2, 2], solos: 18,
    expectedTeams: 20, expectedBracket: 16, expectedTapered: 4,
    forfeitChance: 0,
  },
  'perfect_16_forfeits': { 
    label: '16 Teams + Forfeits (20%)', 
    fullTeams: 16, partials: [], solos: 0,
    expectedTeams: 16, expectedBracket: 16, expectedTapered: 0,
    forfeitChance: 0.20,
    forfeitType: 'single',
  },
  'perfect_16_black_holes': { 
    label: '16 Teams + Black Holes (10%)', 
    fullTeams: 16, partials: [], solos: 0,
    expectedTeams: 16, expectedBracket: 16, expectedTapered: 0,
    forfeitChance: 0.10,
    forfeitType: 'both', // Both teams forfeit = bracket hole
  },
  
  // === VERY LARGE BRACKETS (32 teams) ===
  'perfect_32': { 
    label: '32 Full Teams (5 rounds)', 
    fullTeams: 32, partials: [], solos: 0,
    expectedTeams: 32, expectedBracket: 32, expectedTapered: 0,
    forfeitChance: 0,
  },
  'mixed_32': { 
    label: '32 Mixed Teams', 
    fullTeams: 20, partials: [4, 4, 3, 3, 3, 2, 2, 2, 2], solos: 30,
    expectedTeams: 32, expectedBracket: 32, expectedTapered: 0,
    forfeitChance: 0,
  },
  'perfect_32_chaos': { 
    label: '32 Teams + Chaos (30% forfeit)', 
    fullTeams: 32, partials: [], solos: 0,
    expectedTeams: 32, expectedBracket: 32, expectedTapered: 0,
    forfeitChance: 0.30,
    forfeitType: 'mixed', // Mix of single forfeits and bracket holes
  },
  
  // === SCALE TEST BRACKETS (64+ teams) ===
  'perfect_64': { 
    label: '64 Full Teams (6 rounds)', 
    fullTeams: 64, partials: [], solos: 0,
    expectedTeams: 64, expectedBracket: 64, expectedTapered: 0,
    forfeitChance: 0,
  },
  'perfect_64_chaos': { 
    label: '64 Teams + Chaos (20% forfeit)', 
    fullTeams: 64, partials: [], solos: 0,
    expectedTeams: 64, expectedBracket: 64, expectedTapered: 0,
    forfeitChance: 0.20,
    forfeitType: 'mixed',
  },
  'perfect_128': { 
    label: '128 Full Teams (7 rounds)', 
    fullTeams: 128, partials: [], solos: 0,
    expectedTeams: 128, expectedBracket: 128, expectedTapered: 0,
    forfeitChance: 0,
  },
  'perfect_128_chaos': { 
    label: '128 Teams + Chaos (15% forfeit)', 
    fullTeams: 128, partials: [], solos: 0,
    expectedTeams: 128, expectedBracket: 128, expectedTapered: 0,
    forfeitChance: 0.15,
    forfeitType: 'mixed',
  },
  'perfect_256': { 
    label: '256 Full Teams (8 rounds)', 
    fullTeams: 256, partials: [], solos: 0,
    expectedTeams: 256, expectedBracket: 256, expectedTapered: 0,
    forfeitChance: 0,
  },
  'perfect_256_chaos': { 
    label: '256 Teams + Chaos (10% forfeit)', 
    fullTeams: 256, partials: [], solos: 0,
    expectedTeams: 256, expectedBracket: 256, expectedTapered: 0,
    forfeitChance: 0.10,
    forfeitType: 'mixed',
  },
  'perfect_512': { 
    label: '512 Full Teams (9 rounds)', 
    fullTeams: 512, partials: [], solos: 0,
    expectedTeams: 512, expectedBracket: 512, expectedTapered: 0,
    forfeitChance: 0,
  },
  
  // === SOLO SCALE TESTS (1v1 brackets) ===
  'solo_64': { 
    label: '64 Solo Players (1v1, 6 rounds)', 
    fullTeams: 0, partials: [], solos: 64,
    expectedTeams: 64, expectedBracket: 64, expectedTapered: 0,
    forfeitChance: 0,
    isSoloTest: true, // Flag to use teamSize=1
  },
  'solo_128': { 
    label: '128 Solo Players (1v1, 7 rounds)', 
    fullTeams: 0, partials: [], solos: 128,
    expectedTeams: 128, expectedBracket: 128, expectedTapered: 0,
    forfeitChance: 0,
    isSoloTest: true,
  },
  'solo_256': { 
    label: '256 Solo Players (1v1, 8 rounds)', 
    fullTeams: 0, partials: [], solos: 256,
    expectedTeams: 256, expectedBracket: 256, expectedTapered: 0,
    forfeitChance: 0,
    isSoloTest: true,
  },
  
  // === FLOOD THE POOL TESTS ===
  'flood_8': { 
    label: '8 Teams + Flood the Pool', 
    fullTeams: 8, partials: [], solos: 0,
    expectedTeams: 8, expectedBracket: 8, expectedTapered: 0,
    forfeitChance: 0,
    floodEnabled: true,
    floodViewers: 10,           // 10 viewers contributing
    floodIntensity: 'normal',   // normal contribution amounts
  },
  'flood_16': { 
    label: '16 Teams + Flood the Pool', 
    fullTeams: 16, partials: [], solos: 0,
    expectedTeams: 16, expectedBracket: 16, expectedTapered: 0,
    forfeitChance: 0,
    floodEnabled: true,
    floodViewers: 20,
    floodIntensity: 'normal',
  },
  'flood_32_tsunami': { 
    label: '32 Teams + TSUNAMI Test', 
    fullTeams: 32, partials: [], solos: 0,
    expectedTeams: 32, expectedBracket: 32, expectedTapered: 0,
    forfeitChance: 0,
    floodEnabled: true,
    floodViewers: 50,           // Many viewers
    floodIntensity: 'whale',    // High contribution amounts (hit milestones!)
  },
  'flood_32_chaos': { 
    label: '32 Teams + Flood + Chaos', 
    fullTeams: 32, partials: [], solos: 0,
    expectedTeams: 32, expectedBracket: 32, expectedTapered: 0,
    forfeitChance: 0.20,
    forfeitType: 'mixed',
    floodEnabled: true,
    floodViewers: 30,
    floodIntensity: 'normal',
  },
  
  // === LEGACY ===
  'taper_to_4': { 
    label: 'Taper to 4 (6 teams formed)', 
    fullTeams: 3, partials: [4, 3], solos: 5,
    expectedTeams: 6, expectedBracket: 4, expectedTapered: 2,
    forfeitChance: 0,
  },
  'incomplete': { 
    label: 'Incomplete (some refunded)', 
    fullTeams: 2, partials: [3], solos: 1,
    expectedTeams: 2, expectedBracket: 2, expectedTapered: 0,
    forfeitChance: 0,
  },
};

// ============================================================================
// STEP TRACKER COMPONENT
// ============================================================================

const StepTracker = ({ steps, currentStep, stepResults }) => (
  <View style={styles.stepTracker}>
    {steps.map((step, index) => {
      const isComplete = stepResults[step.id]?.status === 'success';
      const isFailed = stepResults[step.id]?.status === 'error';
      const isCurrent = index === currentStep;
      const isPending = index > currentStep;
      
      return (
        <View key={step.id} style={styles.stepRow}>
          <View style={[
            styles.stepDot,
            isComplete && styles.stepDotComplete,
            isFailed && styles.stepDotFailed,
            isCurrent && styles.stepDotCurrent,
            isPending && styles.stepDotPending,
          ]}>
            {isComplete && <Ionicons name="checkmark" size={12} color="#000" />}
            {isFailed && <Ionicons name="close" size={12} color="#fff" />}
            {!isComplete && !isFailed && <Text style={styles.stepDotText}>{index + 1}</Text>}
          </View>
          <View style={styles.stepContent}>
            <Text style={[
              styles.stepLabel,
              isCurrent && styles.stepLabelCurrent,
              isPending && styles.stepLabelPending,
            ]}>
              {step.label}
            </Text>
            {stepResults[step.id]?.message && (
              <Text style={[
                styles.stepMessage,
                isFailed && styles.stepMessageError,
              ]}>
                {stepResults[step.id].message}
              </Text>
            )}
          </View>
          {index < steps.length - 1 && (
            <View style={[styles.stepLine, isComplete && styles.stepLineComplete]} />
          )}
        </View>
      );
    })}
  </View>
);

// ============================================================================
// LOG PANEL COMPONENT
// ============================================================================

const LogPanel = ({ logs, onClear }) => {
  const scrollRef = useRef(null);
  
  useEffect(() => {
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [logs]);
  
  return (
    <View style={styles.logPanel}>
      <View style={styles.logHeader}>
        <Text style={styles.logTitle}>
          <Ionicons name="terminal" size={14} color={COLORS.primary} /> Test Log
        </Text>
        <TouchableOpacity onPress={onClear}>
          <Text style={styles.logClear}>Clear</Text>
        </TouchableOpacity>
      </View>
      <ScrollView 
        ref={scrollRef}
        style={styles.logScroll} 
        nestedScrollEnabled
      >
        {logs.map((log, i) => (
          <Text key={i} style={[
            styles.logLine,
            log.type === 'success' && styles.logSuccess,
            log.type === 'warning' && styles.logWarning,
            log.type === 'error' && styles.logError,
            log.type === 'info' && styles.logInfo,
            log.type === 'purple' && styles.logPurple,
          ]}>
            <Text style={styles.logTime}>[{log.time}]</Text> {log.message}
          </Text>
        ))}
        {logs.length === 0 && (
          <Text style={styles.logEmpty}>Ready to run tests...</Text>
        )}
      </ScrollView>
    </View>
  );
};

// ============================================================================
// VERIFICATION RESULT COMPONENT
// ============================================================================

const VerificationResult = ({ title, checks }) => (
  <View style={styles.verifyCard}>
    <Text style={styles.verifyTitle}>{title}</Text>
    {checks.map((check, i) => (
      <View key={i} style={styles.verifyRow}>
        <Ionicons 
          name={check.pass ? 'checkmark-circle' : 'close-circle'} 
          size={16} 
          color={check.pass ? COLORS.success : COLORS.danger} 
        />
        <Text style={[styles.verifyText, !check.pass && styles.verifyTextFail]}>
          {check.label}: {check.actual}
          {check.expected !== undefined && ` (expected: ${check.expected})`}
        </Text>
      </View>
    ))}
  </View>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TeamTournamentTester({ userId }) {
  const functions = getFunctions();
  
  // Test configuration
  const [teamSize, setTeamSize] = useState(6);
  const [scenario, setScenario] = useState('mixed_small');
  
  // Test state
  const [testTournamentId, setTestTournamentId] = useState(null);
  const [tournamentData, setTournamentData] = useState(null);
  const [entriesData, setEntriesData] = useState([]);
  const [matchesData, setMatchesData] = useState([]);
  const [pickupGroups, setPickupGroups] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [stepResults, setStepResults] = useState({});
  const [logs, setLogs] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  
  // Firestore listener cleanup
  const unsubscribeRef = useRef(null);
  
  const steps = [
    { id: 'create', label: 'Create Tournament' },
    { id: 'entries', label: 'Add Test Entries' },
    { id: 'lock', label: 'Lock & Match Groups' },
    { id: 'seed', label: 'Seed Bracket' },
    { id: 'verify_bracket', label: 'Verify Bracket' },
    { id: 'simulate', label: 'Simulate Round 1' },
    { id: 'verify_advance', label: 'Verify Advancement' },
    { id: 'complete_finals', label: 'Complete Finals' },
    { id: 'verify_payout', label: 'Verify Payouts' },
  ];
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);
  
  // Add log entry
  const log = useCallback((message, type = 'info') => {
    const time = new Date().toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
    setLogs(prev => [...prev, { time, message, type }]);
  }, []);
  
  // Update step result
  const setStepResult = useCallback((stepId, status, message) => {
    setStepResults(prev => ({
      ...prev,
      [stepId]: { status, message }
    }));
  }, []);
  
  // Clear all state
  const clearAll = useCallback(() => {
    setTestTournamentId(null);
    setTournamentData(null);
    setEntriesData([]);
    setMatchesData([]);
    setPickupGroups([]);
    setCurrentStep(-1);
    setStepResults({});
    setLogs([]);
    setVerifications([]);
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);
  
  // =========================================================================
  // MATCH LIFECYCLE CONSTANTS (mirrors matchReadySystem.js)
  // =========================================================================
  
  const READY_STATUS = {
    WAITING: 'waiting',
    PARTIAL: 'partial', 
    ALL_READY: 'all_ready',
    VERIFYING: 'verifying',
    VERIFIED: 'verified',
    VERIFICATION_FAILED: 'verification_failed',
    TIMEOUT: 'timeout',
  };
  
  const MATCH_STATUS = {
    PENDING: 'pending',
    LOCKED: 'locked',
    LIVE: 'live',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
  };

  // =========================================================================
  // HELPER: Initialize Ready State for Match
  // Simulates what matchReadySystem.initializeReadyState does
  // =========================================================================
  
  const initializeMatchReadyState = useCallback(async (matchRef, matchData, tournamentId) => {
    const p1Roster = matchData.p1?.roster || [];
    const p2Roster = matchData.p2?.roster || [];
    const allParticipants = [...p1Roster, ...p2Roster];
    
    if (allParticipants.length === 0) {
      // Solo match fallback
      if (matchData.p1?.userId) allParticipants.push(matchData.p1.userId);
      if (matchData.p2?.userId) allParticipants.push(matchData.p2.userId);
    }
    
    // Build playersReadyState map
    const playersReadyState = {};
    
    for (const uid of allParticipants) {
      const isP1 = p1Roster.includes(uid) || uid === matchData.p1?.userId;
      const team = isP1 ? matchData.p1 : matchData.p2;
      
      playersReadyState[uid] = {
        displayName: team?.displayName || 'Player',
        ign: `IGN_${uid.slice(-6)}`, // Simulated IGN
        teamId: team?.pickupGroupId || null,
        teamName: team?.displayName || null,
        side: isP1 ? 'p1' : 'p2',
        isCaptain: uid === (team?.captainId || team?.roster?.[0]),
        ready: false,
        readyAt: null,
        streamActive: false,
      };
    }
    
    // Calculate ready deadline (5 minutes from now)
    const readyDeadline = new Date(Date.now() + 5 * 60 * 1000);
    
    await updateDoc(matchRef, {
      status: MATCH_STATUS.LOCKED,
      readyStatus: READY_STATUS.WAITING,
      playersReadyState,
      totalPlayersRequired: allParticipants.length,
      playersReady: 0,
      readyDeadline: Timestamp.fromDate(readyDeadline),
      readyInitializedAt: serverTimestamp(),
      participantUids: allParticipants,
    });
    
    return { playersReadyState, participantCount: allParticipants.length };
  }, []);

  // =========================================================================
  // HELPER: Simulate Players Readying Up
  // Each player confirms ready (with simulated stream validation)
  // =========================================================================
  
  const simulatePlayersReadyUp = useCallback(async (matchRef, playersReadyState, options = {}) => {
    const { 
      noShowPlayers = [],  // Array of UIDs who won't ready
      delayBetweenReady = 100, // ms between each ready
    } = options;
    
    const allUids = Object.keys(playersReadyState);
    const readyingPlayers = allUids.filter(uid => !noShowPlayers.includes(uid));
    
    let readyCount = 0;
    // Deep copy to avoid mutating original
    const updatedReadyState = JSON.parse(JSON.stringify(playersReadyState));
    
    for (const uid of readyingPlayers) {
      // Simulate stream activation
      updatedReadyState[uid] = {
        ...updatedReadyState[uid],
        ready: true,
        readyAt: Timestamp.now(),
        streamActive: true,
        streamValidatedAt: Timestamp.now(),
      };
      readyCount++;
      
      // Brief delay between players readying (simulates real behavior)
      if (delayBetweenReady > 0) {
        await new Promise(r => setTimeout(r, delayBetweenReady));
      }
    }
    
    const totalRequired = allUids.length;
    const allReady = readyCount >= totalRequired;
    
    // Determine ready status
    let readyStatus;
    if (noShowPlayers.length > 0 && noShowPlayers.length === allUids.length) {
      readyStatus = READY_STATUS.TIMEOUT;
    } else if (allReady) {
      readyStatus = READY_STATUS.ALL_READY;
    } else if (readyCount > 0) {
      readyStatus = READY_STATUS.PARTIAL;
    } else {
      readyStatus = READY_STATUS.WAITING;
    }
    
    await updateDoc(matchRef, {
      playersReadyState: updatedReadyState,
      playersReady: readyCount,
      readyStatus,
      ...(allReady && { allReadyAt: serverTimestamp() }),
    });
    
    // Return the updated state so verification can use it
    return { readyCount, totalRequired, allReady, readyStatus, updatedReadyState };
  }, []);

  // =========================================================================
  // HELPER: Simulate Vision Verification
  // Simulates checking streams for IGN verification
  // =========================================================================
  
  const simulateVisionVerification = useCallback(async (matchRef, playersReadyState, options = {}) => {
    const {
      verificationDelay = 500, // ms to simulate verification time
      failVerification = false, // Force verification failure
      partialVerification = false, // Only some players verified
    } = options;
    
    // Set status to verifying
    await updateDoc(matchRef, {
      readyStatus: READY_STATUS.VERIFYING,
      verificationStartedAt: serverTimestamp(),
    });
    
    // Simulate verification processing time
    await new Promise(r => setTimeout(r, verificationDelay));
    
    // Build verification results
    const verificationResults = {};
    const readyPlayers = Object.entries(playersReadyState)
      .filter(([_, state]) => state.ready);
    
    let verifiedCount = 0;
    
    for (const [uid, state] of readyPlayers) {
      const shouldVerify = !failVerification && (!partialVerification || Math.random() > 0.2);
      
      verificationResults[uid] = {
        verified: shouldVerify,
        ignDetected: shouldVerify ? state.ign : null,
        ignExpected: state.ign,
        confidence: shouldVerify ? 0.85 + Math.random() * 0.15 : 0.3,
        verifiedAt: Timestamp.now(),
      };
      
      if (shouldVerify) verifiedCount++;
    }
    
    const allVerified = verifiedCount === readyPlayers.length;
    const verificationPassed = verifiedCount >= readyPlayers.length * 0.8; // 80% threshold
    
    const finalStatus = verificationPassed ? READY_STATUS.VERIFIED : READY_STATUS.VERIFICATION_FAILED;
    
    await updateDoc(matchRef, {
      readyStatus: finalStatus,
      verificationResults,
      verificationCompletedAt: serverTimestamp(),
      verificationPassed,
      playersVerified: verifiedCount,
      ...(verificationPassed && { status: MATCH_STATUS.LIVE, liveAt: serverTimestamp() }),
    });
    
    return { verifiedCount, totalPlayers: readyPlayers.length, verificationPassed, finalStatus };
  }, []);

  // =========================================================================
  // HELPER: Complete Match After Live Status
  // Handles the final match result submission
  // =========================================================================
  
  const completeMatchWithResult = useCallback(async (matchRef, matchData, winner = 'p1') => {
    const winningTeam = winner === 'p1' ? matchData.p1 : matchData.p2;
    const losingTeam = winner === 'p1' ? matchData.p2 : matchData.p1;
    
    const winnerId = winningTeam?.roster?.[0] || winningTeam?.captainId || winningTeam?.userId;
    const loserId = losingTeam?.roster?.[0] || losingTeam?.captainId || losingTeam?.userId;
    const hostUid = matchData.hostUid || winnerId;
    
    await updateDoc(matchRef, {
      status: MATCH_STATUS.COMPLETED,
      winnerId: winnerId || null,
      winnerPickupGroupId: winningTeam?.pickupGroupId || null,
      loserId: loserId || null,
      loserPickupGroupId: losingTeam?.pickupGroupId || null,
      hostUid: hostUid || null,
      completedAt: serverTimestamp(),
      matchDurationSeconds: Math.floor(180 + Math.random() * 300), // 3-8 min simulated
    });
    
    return { winnerId, winnerTeam: winningTeam?.displayName, loserTeam: losingTeam?.displayName };
  }, []);

  // =========================================================================
  // HELPER: Full Match Lifecycle Simulation
  // Runs through: locked -> ready -> verified -> live -> completed
  // =========================================================================
  
  const simulateFullMatchLifecycle = useCallback(async (matchRef, matchData, tournamentId, options = {}) => {
    const {
      winner = 'p1', // 'p1', 'p2', 'forfeit_p1', 'forfeit_p2', 'both_no_show'
      skipReadyUp = false,
      skipVerification = false,
      logFn = null, // Optional logging function
    } = options;
    
    const matchId = matchRef.id || matchRef.path?.split('/').pop();
    const isTeamMatch = !!(matchData.p1?.pickupGroupId || matchData.p2?.pickupGroupId);
    const teamSize = matchData.p1?.roster?.length || 1;
    const totalPlayers = teamSize * 2;
    
    // Track lifecycle stages for verification
    const lifecycle = [];
    
    // ===== PHASE 1: Initialize Ready State =====
    if (!skipReadyUp) {
      lifecycle.push('locked');
      
      const { playersReadyState, participantCount } = await initializeMatchReadyState(
        matchRef, matchData, tournamentId
      );
      
      if (logFn) logFn(`      [READY] Initialized ${participantCount} players`, 'info');
      lifecycle.push('waiting');
      
      // ===== PHASE 2: Players Ready Up =====
      let noShowPlayers = [];
      
      if (winner === 'forfeit_p1') {
        // P1 team no-shows
        noShowPlayers = matchData.p1?.roster || [matchData.p1?.userId].filter(Boolean);
      } else if (winner === 'forfeit_p2') {
        // P2 team no-shows  
        noShowPlayers = matchData.p2?.roster || [matchData.p2?.userId].filter(Boolean);
      } else if (winner === 'both_no_show') {
        // Everyone no-shows
        noShowPlayers = Object.keys(playersReadyState);
      }
      
      const readyResult = await simulatePlayersReadyUp(matchRef, playersReadyState, {
        noShowPlayers,
        delayBetweenReady: 30,
      });
      
      if (logFn) logFn(`      [READY] ${readyResult.readyCount}/${readyResult.totalRequired} ready`, 'info');
      
      // Handle no-show cases
      if (winner === 'both_no_show') {
        lifecycle.push('timeout');
        // Both teams no-show = bracket hole
        await updateDoc(matchRef, {
          status: MATCH_STATUS.COMPLETED,
          completedReason: 'both_no_show',
          isBracketHole: true,
          forfeit: true,
          winnerId: null,
          winnerPickupGroupId: null,
          loserPickupGroupId: null,
          completedAt: serverTimestamp(),
        });
        lifecycle.push('completed');
        return { outcome: 'bracket_hole', lifecycle };
      }
      
      if (winner === 'forfeit_p1' || winner === 'forfeit_p2') {
        lifecycle.push('partial');
        lifecycle.push('timeout');
        // One team no-shows = forfeit
        const winningTeam = winner === 'forfeit_p1' ? matchData.p2 : matchData.p1;
        const losingTeam = winner === 'forfeit_p1' ? matchData.p1 : matchData.p2;
        
        await updateDoc(matchRef, {
          status: MATCH_STATUS.COMPLETED,
          completedReason: 'forfeit_no_show',
          forfeit: true,
          winnerId: winningTeam?.roster?.[0] || winningTeam?.userId,
          winnerPickupGroupId: winningTeam?.pickupGroupId || null,
          loserPickupGroupId: losingTeam?.pickupGroupId || null,
          forfeitTeamId: losingTeam?.pickupGroupId,
          hostUid: winningTeam?.roster?.[0] || winningTeam?.userId,
          completedAt: serverTimestamp(),
        });
        lifecycle.push('completed');
        return { outcome: 'forfeit', winner: winningTeam?.displayName, lifecycle };
      }
      
      lifecycle.push('all_ready');
      
      // ===== PHASE 3: Vision Verification =====
      if (!skipVerification && readyResult.allReady) {
        lifecycle.push('verifying');
        
        // IMPORTANT: Use the updatedReadyState from simulatePlayersReadyUp, not the original
        const verifyResult = await simulateVisionVerification(matchRef, readyResult.updatedReadyState, {
          verificationDelay: 150,
        });
        
        if (logFn) logFn(`      [VERIFY] ${verifyResult.verifiedCount}/${verifyResult.totalPlayers} verified`, 'info');
        lifecycle.push('verified');
      } else if (readyResult.allReady) {
        // Skip verification but still go live
        await updateDoc(matchRef, {
          readyStatus: READY_STATUS.VERIFIED,
          verificationSkipped: true,
          status: MATCH_STATUS.LIVE,
          liveAt: serverTimestamp(),
        });
        lifecycle.push('verified');
      }
      
      // ===== PHASE 4: Match Goes Live =====
      lifecycle.push('live');
      if (logFn) logFn(`      [LIVE] Match is now live`, 'info');
      
      // Small delay to simulate match being played
      await new Promise(r => setTimeout(r, 100));
    }
    
    // ===== PHASE 5: Match Completion =====
    const result = await completeMatchWithResult(matchRef, matchData, winner);
    lifecycle.push('completed');
    
    return { 
      outcome: 'normal', 
      winner: result.winnerTeam,
      loser: result.loserTeam,
      lifecycle,
    };
  }, [initializeMatchReadyState, simulatePlayersReadyUp, simulateVisionVerification, completeMatchWithResult]);
  
  // =========================================================================
  // FLOOD THE POOL SIMULATION FUNCTIONS
  // =========================================================================
  
  // Track simulated viewers
  const [viewerUsers, setViewerUsers] = useState([]);
  const [floodStats, setFloodStats] = useState({
    totalContributions: 0,
    totalAmountCents: 0,
    milestonesHit: [],
    waveBreakdown: { ripple: 0, wave: 0, surge: 0, tsunami: 0 },
  });
  
  /**
   * Create simulated viewer data (in-memory only - no Firestore writes needed)
   * These simulate "fans" watching the tournament who will flood the pool
   * 
   * NOTE: We don't create actual user documents because:
   * 1. Client-side code can't write to 'users' collection (security rules)
   * 2. For testing purposes, we only need to simulate the flood contributions
   * 3. The flood distribution system reads from tournament.floodTotalCents, not user balances
   */
  const createViewerUsers = useCallback(async (count, tournamentId) => {
    log(`Simulating ${count} viewers (in-memory, no DB writes)...`, 'info');
    
    const viewers = [];
    
    for (let i = 0; i < count; i++) {
      const viewerId = `simulated_viewer_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      const viewerName = getViewerName(i);
      
      // Give viewers varying wallet balances (simulated)
      // Some whales, some regular contributors
      const isWhale = Math.random() < 0.1; // 10% are whales
      const balanceCents = isWhale 
        ? Math.floor(50000 + Math.random() * 200000) // $500-$2500 for whales
        : Math.floor(1000 + Math.random() * 10000);  // $10-$110 for regular
      
      viewers.push({ 
        id: viewerId, 
        displayName: viewerName, 
        balanceCents, 
        isWhale,
        isSimulated: true, // Flag to indicate this is simulated
      });
    }
    
    const whaleCount = viewers.filter(v => v.isWhale).length;
    const totalBalance = viewers.reduce((sum, v) => sum + v.balanceCents, 0);
    log(`  Simulated ${viewers.length} viewers (${whaleCount} whales)`, 'success');
    log(`  Total simulated wallet balance: $${(totalBalance / 100).toFixed(2)}`, 'info');
    
    setViewerUsers(viewers);
    return viewers;
  }, [log]);
  
  /**
   * Simulate a single flood contribution
   * Updates tournament flood totals directly (skips subcollection to avoid permission issues)
   * 
   * NOTE: In production, the cloud function writes to floodContributions subcollection.
   * For testing, we skip that and just update the totals on the tournament document,
   * which is sufficient to verify the flood distribution system works.
   */
  const simulateFloodContribution = useCallback(async (
    tournamentId, 
    viewerId, 
    viewerName, 
    amountCents,
    tournamentRef,
    currentFloodTotal // Pass this in to avoid extra reads
  ) => {
    const platformFeeCents = Math.floor(amountCents * (FLOOD_CONFIG.PLATFORM_FEE_PERCENT / 100));
    const netContributionCents = amountCents - platformFeeCents;
    const waveType = getWaveType(amountCents);
    
    const newFloodTotal = currentFloodTotal + netContributionCents;
    
    // Check for tsunami milestone trigger
    const prevMilestone = FLOOD_CONFIG.MILESTONES.filter(m => m <= currentFloodTotal).pop() || 0;
    const newMilestone = FLOOD_CONFIG.MILESTONES.filter(m => m <= newFloodTotal).pop() || 0;
    const tsunamiTriggered = newMilestone > prevMilestone;
    
    // NOTE: We skip writing to floodContributions subcollection because:
    // 1. Client-side code can't write there (security rules)
    // 2. The flood distribution system reads from tournament.floodTotalCents
    // 3. For testing, updating the totals is sufficient
    
    // Update tournament flood totals only
    await updateDoc(tournamentRef, {
      floodTotalCents: increment(netContributionCents),
      floodGrossCents: increment(amountCents),
      floodPlatformFeeCents: increment(platformFeeCents),
      floodCount: increment(1),
      floodLastContribution: {
        displayName: viewerName,
        amountCents,
        netContributionCents,
        waveType: waveType.type,
        waveIcon: waveType.icon,
        createdAt: serverTimestamp(),
      },
      ...(tsunamiTriggered && {
        floodCurrentMilestone: newMilestone,
        floodLastTsunamiAt: serverTimestamp(),
      }),
    });
    
    return {
      amountCents,
      netContributionCents,
      waveType,
      tsunamiTriggered,
      milestoneReached: tsunamiTriggered ? newMilestone : null,
      newFloodTotal,
    };
  }, []);
  
  /**
   * Simulate multiple viewers flooding during a live match
   */
  const simulateFloodDuringMatch = useCallback(async (
    tournamentId,
    matchId,
    viewers,
    intensity = 'normal'
  ) => {
    if (!viewers || viewers.length === 0) return { contributions: 0, totalCents: 0 };
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    let contributions = 0;
    let totalCents = 0;
    let milestonesHit = [];
    const waves = { ripple: 0, wave: 0, surge: 0, tsunami: 0 };
    
    // Determine how many viewers contribute this match
    const contributingCount = Math.ceil(viewers.length * FLOOD_CONFIG.CONTRIBUTION_CHANCE);
    const shuffledViewers = [...viewers].sort(() => Math.random() - 0.5).slice(0, contributingCount);
    
    for (const viewer of shuffledViewers) {
      // Skip if viewer doesn't have enough balance
      if (viewer.balanceCents < FLOOD_CONFIG.MIN_CONTRIBUTION) continue;
      
      // Generate contribution amount based on intensity
      let amountCents;
      if (intensity === 'whale') {
        // Whale mode: higher amounts, more tsunamis
        amountCents = viewer.isWhale 
          ? Math.floor(10000 + Math.random() * 40000)  // $100-$500 for whales
          : Math.floor(500 + Math.random() * 5000);    // $5-$55 for regulars
      } else {
        // Normal mode: use weighted distribution
        amountCents = getRandomContributionAmount();
      }
      
      // Cap at viewer's balance
      amountCents = Math.min(amountCents, viewer.balanceCents);
      if (amountCents < FLOOD_CONFIG.MIN_CONTRIBUTION) continue;
      
      try {
        const result = await simulateFloodContribution(
          tournamentId,
          viewer.id,
          viewer.displayName,
          amountCents,
          tournamentRef
        );
        
        contributions++;
        totalCents += result.netContributionCents;
        waves[result.waveType.type]++;
        
        // Update viewer's local balance tracking
        viewer.balanceCents -= amountCents;
        
        if (result.tsunamiTriggered) {
          milestonesHit.push(result.milestoneReached);
        }
      } catch (error) {
        // Log errors visibly (first error only to avoid spam)
        if (contributions === 0) {
          // First contribution failed - likely a permission issue
          return { 
            contributions: 0, 
            totalCents: 0, 
            milestonesHit: [], 
            waves,
            error: error.message,
            errorCode: error.code || 'unknown',
          };
        }
        // Subsequent failures - just skip
      }
    }
    
    return { contributions, totalCents, milestonesHit, waves };
  }, [simulateFloodContribution]);
  
  /**
   * Verify flood pool was distributed correctly to winners
   */
  const verifyFloodDistribution = useCallback(async (tournamentId, expectedFloodTotal) => {
    log('Verifying flood pool distribution...', 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const tournamentSnap = await getDoc(tournamentRef);
    const tournament = tournamentSnap.data();
    
    const checks = [];
    
    // Check 1: Flood total matches expected
    const actualFloodTotal = tournament.floodTotalCents || 0;
    checks.push({
      label: 'Flood total recorded',
      expected: `~$${(expectedFloodTotal / 100).toFixed(2)}`,
      actual: `$${(actualFloodTotal / 100).toFixed(2)}`,
      pass: actualFloodTotal > 0,
    });
    
    // Check 2: Flood distribution flag
    const floodDistributed = tournament.floodDistributed === true;
    checks.push({
      label: 'Flood distributed flag',
      expected: 'true',
      actual: String(floodDistributed),
      pass: floodDistributed,
    });
    
    // Check 3: Get flood contributions count
    const contributionsSnap = await getDocs(collection(tournamentRef, 'floodContributions'));
    const contributionCount = contributionsSnap.size;
    checks.push({
      label: 'Flood contributions recorded',
      expected: '> 0',
      actual: contributionCount.toString(),
      pass: contributionCount > 0,
    });
    
    // Check 4: Verify champion received flood bonus
    if (tournament.championRosterUids && tournament.championRosterUids.length > 0) {
      const championUid = tournament.championRosterUids[0];
      const championRef = doc(db, 'users', championUid);
      const championSnap = await getDoc(championRef);
      
      if (championSnap.exists()) {
        // Check for flood_payout transaction
        const txSnap = await getDocs(
          query(
            collection(championRef, 'transactions'),
            where('type', '==', 'flood_payout')
          )
        );
        
        const hasFloodPayout = txSnap.size > 0;
        const floodPayoutAmount = hasFloodPayout 
          ? txSnap.docs.reduce((sum, d) => sum + (d.data().amountCents || 0), 0)
          : 0;
        
        checks.push({
          label: 'Champion received flood payout',
          expected: 'Yes',
          actual: hasFloodPayout ? `Yes ($${(floodPayoutAmount / 100).toFixed(2)})` : 'No',
          pass: hasFloodPayout,
        });
      }
    }
    
    // Log wave breakdown
    const allPass = checks.every(c => c.pass);
    if (allPass) {
      log('Flood distribution verification PASSED', 'success');
    } else {
      const failed = checks.filter(c => !c.pass);
      log(`Flood distribution FAILED: ${failed.map(f => f.label).join(', ')}`, 'error');
    }
    
    return { allPass, checks, floodTotal: actualFloodTotal };
  }, [log]);
  
  // =========================================================================
  // STEP 1: CREATE TEST TOURNAMENT
  // =========================================================================
  
  const createTournament = useCallback(async () => {
    log('Creating test team tournament...', 'info');
    
    const tournamentId = `test_team_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const now = new Date();
    const startAt = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
    const regCloseAt = new Date(now.getTime() + 55 * 60 * 1000);
    
    const config = SCENARIOS[scenario];
    
    const tournamentDoc = {
      // Identity
      name: `E2E Test: ${config.label} (${teamSize}v${teamSize})`,
      isTest: true,
      isTeamEvent: true,
      
      // Team config
      teamSize: teamSize,
      entryUnit: 'player',
      
      // Timing
      startAt: Timestamp.fromDate(startAt),
      registrationClosesAt: Timestamp.fromDate(regCloseAt),
      estimatedEndTime: Timestamp.fromDate(new Date(startAt.getTime() + 3 * 60 * 60 * 1000)),
      
      // Limits
      minEntries: teamSize * 2,
      maxEntries: teamSize * 32,
      entrantsCount: 0,
      
      // Entry: free, on the Crit path. Cash entry is retired, and this doc is
      // written straight to Firestore (no callable normalizes it), so it must
      // carry the Crit-path stamps itself. A cash-shaped doc here would send
      // completion through the legacy cash distributor, which credits
      // balanceCents to the test_ uids from a pot that was never collected.
      entryFeeCents: 0,
      critEntryCost: 0,
      entryCurrency: 'crits',
      prizePool: 0,
      
      // Game
      game: 'Test Game',
      gameName: 'Test Game',
      platform: 'PC',
      region: 'NA',
      format: 'bracket',
      
      // Status
      status: 'scheduled',
      
      // Meta
      createdBy: userId,
      createdByAdmin: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      
      // Test tracking
      testScenario: scenario,
      testExpectedTeams: config.expectedTeams,
      testExpectedBracket: config.expectedBracket,
      testExpectedTapered: config.expectedTapered,
    };
    
    await setDoc(doc(db, 'tournaments', tournamentId), tournamentDoc);
    
    setTestTournamentId(tournamentId);
    setTournamentData({ id: tournamentId, ...tournamentDoc });
    
    log(`Tournament created: ${tournamentId}`, 'success');
    log(`Scenario: ${config.label}`, 'info');
    log(`Team size: ${teamSize}v${teamSize}`, 'info');
    log(`Expected teams: ${config.expectedTeams}, Bracket: ${config.expectedBracket}`, 'info');
    
    return tournamentId;
  }, [userId, teamSize, scenario, log]);
  
  // =========================================================================
  // STEP 2: ADD TEST ENTRIES
  // =========================================================================
  
  const addEntries = useCallback(async (tournamentId) => {
    const config = SCENARIOS[scenario];
    const effectiveTeamSize = config.isSoloTest ? 1 : teamSize;
    log(`Adding entries: ${config.fullTeams} full, ${config.partials.length} partials, ${config.solos} solos`, 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const created = [];
    let ordinal = 1;
    let teamCounter = 1;
    
    // For large tournaments, we need to batch writes in groups of 450 (leaving room for overhead)
    const BATCH_SIZE = 450;
    let currentBatch = writeBatch(db);
    let batchCount = 0;
    let totalBatches = 0;
    
    const flushBatch = async () => {
      if (batchCount > 0) {
        await currentBatch.commit();
        totalBatches++;
        log(`  Committed batch ${totalBatches} (${batchCount} entries)`, 'info');
        currentBatch = writeBatch(db);
        batchCount = 0;
      }
    };
    
    const createEntry = async (teamId, teamName) => {
      // Check if we need to flush the batch
      if (batchCount >= BATCH_SIZE) {
        await flushBatch();
      }
      
      const testUserId = `test_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
      const entryRef = doc(collection(tournamentRef, 'entries'), testUserId);
      const userName = getRandomName();
      
      const entryData = {
        userId: testUserId,
        ordinal: ordinal++,
        status: 'registered',
        joinedAt: serverTimestamp(),
        registeredAt: Timestamp.fromMillis(Date.now() - Math.random() * 3600000),
        userName,
        displayName: userName,
        ign: userName,
        teamId,
        teamName,
        isTestEntry: true,
        testCreatedAt: serverTimestamp(),
        streamKeyId: `test_stream_${testUserId}`,
        hasValidStream: false,
      };
      
      currentBatch.set(entryRef, entryData);
      batchCount++;
      created.push({ id: testUserId, teamId, teamName, userName });
    };
    
    // Calculate total entries for progress logging
    const totalEntries = (config.fullTeams * effectiveTeamSize) + 
      config.partials.reduce((sum, p) => sum + p, 0) + 
      config.solos;
    log(`  Total entries to create: ${totalEntries}`, 'info');
    
    // Full teams
    for (let i = 0; i < config.fullTeams; i++) {
      const teamId = `test_team_${teamCounter++}`;
      const teamName = getTeamName(i);
      if (config.fullTeams <= 32) {
        log(`  Full team: ${teamName}`, 'info');
      } else if (i % 32 === 0) {
        log(`  Creating teams ${i + 1} - ${Math.min(i + 32, config.fullTeams)}...`, 'info');
      }
      for (let j = 0; j < effectiveTeamSize; j++) {
        await createEntry(teamId, teamName);
      }
    }
    
    // Partial teams
    for (const partialSize of config.partials) {
      const teamId = `test_team_${teamCounter++}`;
      const teamName = getTeamName(teamCounter - 1);
      log(`  Partial team (${partialSize}): ${teamName}`, 'info');
      for (let j = 0; j < partialSize; j++) {
        await createEntry(teamId, teamName);
      }
    }
    
    // Solos
    if (config.solos > 0) {
      log(`  Solo players: ${config.solos}`, 'info');
      for (let i = 0; i < config.solos; i++) {
        await createEntry(null, null);
      }
    }
    
    // Final batch update - just update timestamp, entrantsCount is handled by entry creation triggers
    currentBatch.update(tournamentRef, {
      updatedAt: serverTimestamp(),
    });
    batchCount++; // Count the update
    await flushBatch();
    
    setEntriesData(created);
    log(`Created ${created.length} entries total (${totalBatches} batches)`, 'success');
    
    return created;
  }, [scenario, teamSize, log]);
  
  // =========================================================================
  // STEP 3: LOCK TOURNAMENT (Triggers pickup matching)
  // =========================================================================
  
  const lockTournament = useCallback(async (tournamentId, expectedTeams = 32) => {
    log('Locking tournament to trigger pickup group matching...', 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    
    // Dynamic timeout based on expected teams
    // Pickup matching + bracket seeding can take significant time for large brackets
    // Base: 30s for small, scaling up for larger brackets
    const timeoutMs = expectedTeams >= 512 ? 300000 :  // 5 minutes for 512+
                      expectedTeams >= 256 ? 180000 :  // 3 minutes for 256+
                      expectedTeams >= 128 ? 90000 :   // 90 seconds for 128+
                      expectedTeams >= 64 ? 60000 :    // 60 seconds for 64+
                      30000;                            // 30 seconds for smaller
    
    log(`  Lock timeout: ${timeoutMs / 1000}s for ${expectedTeams}-team bracket`, 'info');
    
    // Set up listener for status changes
    return new Promise((resolve, reject) => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Timeout waiting for lock processing'));
        }
      }, timeoutMs);
      
      unsubscribeRef.current = onSnapshot(tournamentRef, async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        
        setTournamentData({ id: tournamentId, ...data });
        
        if (data.status === 'locked' && data.pickupGroupsFormed) {
          log(`Pickup groups formed: ${data.pickupGroupsFormed}`, 'success');
        }
        
        if (data.status === 'seeded' && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          
          // Load entries to see pickup group assignments
          const entriesSnap = await getDocs(collection(tournamentRef, 'entries'));
          const entries = entriesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setEntriesData(entries);
          
          // Analyze pickup groups
          const groupMap = new Map();
          for (const entry of entries) {
            if (entry.pickupGroupId) {
              if (!groupMap.has(entry.pickupGroupId)) {
                groupMap.set(entry.pickupGroupId, {
                  id: entry.pickupGroupId,
                  type: entry.pickupGroupType,
                  priority: entry.pickupGroupPriority,
                  displayName: entry.teamName || entry.pickupGroupDisplayName || entry.pickupGroupId,
                  members: [],
                  seed: entry.pickupGroupSeed,
                });
              }
              groupMap.get(entry.pickupGroupId).members.push(entry);
            }
          }
          
          const groups = Array.from(groupMap.values()).sort((a, b) => (a.seed || 99) - (b.seed || 99));
          setPickupGroups(groups);
          
          log(`Seeded with ${groups.length} teams`, 'success');
          groups.forEach((g, i) => {
            log(`  #${g.seed || i + 1} ${g.displayName} (P${g.priority}, ${g.type})`, 'info');
          });
          
          resolve(data);
        }
      });
      
      // Trigger lock
      updateDoc(tournamentRef, {
        status: 'locked',
        lockedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }).catch(reject);
    });
  }, [log]);
  
  // =========================================================================
  // STEP 4: VERIFY BRACKET
  // =========================================================================
  
  const verifyBracket = useCallback(async (tournamentId) => {
    log('Verifying bracket structure...', 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const matchesSnap = await getDocs(collection(tournamentRef, 'matches'));
    const matches = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    setMatchesData(matches);
    
    const config = SCENARIOS[scenario];
    const tournamentSnap = await getDoc(tournamentRef);
    const tData = tournamentSnap.data();
    
    // Verification checks
    const checks = [];
    
    // Check bracket size
    const bracketSize = tData.bracketSize || matches.length;
    checks.push({
      label: 'Bracket size',
      expected: config.expectedBracket,
      actual: bracketSize,
      pass: bracketSize === config.expectedBracket,
    });
    
    // Check teams kept
    const teamsKept = tData.teamCount || pickupGroups.length;
    checks.push({
      label: 'Teams in bracket',
      expected: config.expectedBracket,
      actual: teamsKept,
      pass: teamsKept === config.expectedBracket,
    });
    
    // Check matches count (bracket size - 1 for single elim)
    const expectedMatches = config.expectedBracket - 1;
    checks.push({
      label: 'Match count',
      expected: expectedMatches,
      actual: matches.length,
      pass: matches.length === expectedMatches,
    });
    
    // Check round 1 matches
    const round1 = matches.filter(m => m.round === 1);
    const expectedR1 = config.expectedBracket / 2;
    checks.push({
      label: 'Round 1 matches',
      expected: expectedR1,
      actual: round1.length,
      pass: round1.length === expectedR1,
    });
    
    // Check all matches have proper structure
    const hasTeamMatches = matches.every(m => m.isTeamMatch === true);
    checks.push({
      label: 'All matches marked as team',
      expected: 'true',
      actual: String(hasTeamMatches),
      pass: hasTeamMatches,
    });
    
    // Check R1 matches have both teams
    const r1Complete = round1.every(m => m.p1?.pickupGroupId && (m.p2?.pickupGroupId || m.p2?.isBye));
    checks.push({
      label: 'R1 matches populated',
      expected: 'true',
      actual: String(r1Complete),
      pass: r1Complete,
    });
    
    setVerifications(prev => [...prev, { title: 'Bracket Structure', checks }]);
    
    const allPass = checks.every(c => c.pass);
    if (allPass) {
      log('Bracket verification PASSED', 'success');
    } else {
      const failed = checks.filter(c => !c.pass);
      log(`Bracket verification FAILED: ${failed.map(f => f.label).join(', ')}`, 'error');
    }
    
    return { matches, allPass, checks };
  }, [scenario, pickupGroups, log]);
  
  // =========================================================================
  // STEP 5: SIMULATE MATCHES (with forfeit/bracket hole support)
  // =========================================================================
  
  const simulateRound = useCallback(async (tournamentId, round, options = {}) => {
    const { 
      forfeitChance = 0, 
      forfeitType = 'single', 
      useFullLifecycle = true,
      floodEnabled = false,
      floodViewers = [],
      floodIntensity = 'normal',
    } = options;
    
    const hasForfeitChance = forfeitChance > 0;
    const lifecycleMode = useFullLifecycle ? 'FULL LIFECYCLE' : 'QUICK';
    const floodMode = floodEnabled ? ' + FLOOD' : '';
    log(`Simulating Round ${round} [${lifecycleMode}${floodMode}] (P1 wins${hasForfeitChance ? `, ${Math.round(forfeitChance * 100)}% forfeit chance` : ''})...`, 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const matchesSnap = await getDocs(
      query(
        collection(tournamentRef, 'matches'),
        where('round', '==', round),
        where('status', '!=', 'completed')
      )
    );
    
    const pendingMatches = matchesSnap.docs
      .map(d => ({ id: d.id, ref: doc(tournamentRef, 'matches', d.id), ...d.data() }))
      .filter(m => m.p1 && m.p2 && !m.p2.isBye && !m.p1.isBracketHole && !m.p2.isBracketHole);
    
    if (pendingMatches.length === 0) {
      log(`No pending matches in Round ${round}`, 'warning');
      return [];
    }
    
    const results = [];
    let normalMatches = 0;
    let forfeitsSimulated = 0;
    let bracketHolesCreated = 0;
    let lifecycleStages = { locked: 0, ready: 0, verified: 0, live: 0, completed: 0 };
    
    // Flood tracking for this round
    let roundFloodContributions = 0;
    let roundFloodAmountCents = 0;
    let roundMilestonesHit = [];
    let roundWaves = { ripple: 0, wave: 0, surge: 0, tsunami: 0 };
    
    for (const match of pendingMatches) {
      const matchRef = match.ref;
      
      // Determine match outcome
      const shouldForfeit = hasForfeitChance && Math.random() < forfeitChance;
      const isBracketHole = shouldForfeit && (forfeitType === 'both' || (forfeitType === 'mixed' && Math.random() < 0.3));
      
      let winner = 'p1'; // Default: P1 wins
      if (isBracketHole) {
        winner = 'both_no_show';
      } else if (shouldForfeit) {
        winner = 'forfeit_p2'; // P2 no-shows, P1 wins
      }
      
      if (useFullLifecycle) {
        // ===== FULL LIFECYCLE SIMULATION =====
        // locked -> ready -> verified -> live -> completed
        const lifecycleResult = await simulateFullMatchLifecycle(matchRef, match, tournamentId, {
          winner,
          skipReadyUp: false,
          skipVerification: false,
          logFn: null, // Don't log each phase for cleaner output
        });
        
        // Track lifecycle stages
        for (const stage of lifecycleResult.lifecycle) {
          if (lifecycleStages[stage] !== undefined) lifecycleStages[stage]++;
        }
        
        // ===== FLOOD SIMULATION DURING LIVE MATCH =====
        if (floodEnabled && floodViewers.length > 0 && lifecycleResult.outcome === 'normal') {
          const floodResult = await simulateFloodDuringMatch(
            tournamentId,
            match.id,
            floodViewers,
            floodIntensity
          );
          
          // Check for permission error on first contribution
          if (floodResult.error) {
            log(`    [FLOOD ERROR] ${floodResult.error} (code: ${floodResult.errorCode})`, 'error');
            // Don't try more floods this round
            break;
          }
          
          roundFloodContributions += floodResult.contributions;
          roundFloodAmountCents += floodResult.totalCents;
          roundMilestonesHit.push(...(floodResult.milestonesHit || []));
          
          // Merge wave counts
          for (const [type, count] of Object.entries(floodResult.waves || {})) {
            roundWaves[type] = (roundWaves[type] || 0) + count;
          }
          
          if (floodResult.contributions > 0) {
            log(`    [FLOOD] ${floodResult.contributions} contributions (+$${(floodResult.totalCents / 100).toFixed(2)})`, 'purple');
          }
          if (floodResult.milestonesHit?.length > 0) {
            for (const milestone of floodResult.milestonesHit) {
              log(`    [TSUNAMI] Milestone hit: $${(milestone / 100).toLocaleString()}!`, 'warning');
            }
          }
        }
        
        // Log result
        if (lifecycleResult.outcome === 'bracket_hole') {
          log(`  [BRACKET HOLE] ${match.p1.displayName} vs ${match.p2.displayName} - Both no-show`, 'warning');
          bracketHolesCreated++;
        } else if (lifecycleResult.outcome === 'forfeit') {
          log(`  [FORFEIT] ${lifecycleResult.winner} wins - opponent no-show`, 'warning');
          forfeitsSimulated++;
        } else {
          log(`  ${match.p1.displayName} defeats ${match.p2.displayName}`, 'info');
          normalMatches++;
        }
        
        results.push({
          matchId: match.id,
          result: lifecycleResult.outcome,
          lifecycle: lifecycleResult.lifecycle,
          winner: lifecycleResult.winner,
        });
        
      } else {
        // ===== QUICK SIMULATION (legacy) =====
        // Skip ready-up and verification, directly complete
        if (isBracketHole) {
          await updateDoc(matchRef, {
            winnerId: null,
            winnerPickupGroupId: null,
            loserPickupGroupId: null,
            status: 'completed',
            completedReason: 'both_no_show',
            isBracketHole: true,
            forfeit: true,
            completedAt: serverTimestamp(),
            testCompleted: true,
            testNote: 'Simulated bracket hole (both teams no-show)',
          });
          
          log(`  [BRACKET HOLE] ${match.p1.displayName} vs ${match.p2.displayName} - Both no-show`, 'warning');
          results.push({ matchId: match.id, result: 'bracket_hole' });
          bracketHolesCreated++;
          
        } else if (shouldForfeit) {
          const winnerId = match.p1.pickupGroupId;
          const winnerUserId = match.p1.roster?.[0] || match.p1.captainId || winnerId;
          const loserId = match.p2.pickupGroupId;
          const hostUid = match.hostUid || winnerUserId;
          
          await updateDoc(matchRef, {
            winnerId: winnerUserId || null,
            winnerPickupGroupId: winnerId || null,
            loserPickupGroupId: loserId || null,
            hostUid: hostUid || null,
            status: 'completed',
            completedReason: 'forfeit_no_show',
            forfeit: true,
            forfeitTeamId: loserId,
            completedAt: serverTimestamp(),
            testCompleted: true,
          });
          
          log(`  [FORFEIT] ${match.p1.displayName} wins - ${match.p2.displayName} no-show`, 'warning');
          results.push({ matchId: match.id, winnerId, loserId, hostUid, result: 'forfeit' });
          forfeitsSimulated++;
          
        } else {
          const winnerId = match.p1.pickupGroupId;
          const loserId = match.p2.pickupGroupId;
          const winnerUserId = match.p1.roster?.[0] || match.p1.captainId || winnerId;
          const hostUid = match.hostUid || winnerUserId;
          
          await updateDoc(matchRef, {
            winnerId: winnerUserId || null,
            winnerPickupGroupId: winnerId || null,
            loserPickupGroupId: loserId || null,
            hostUid: hostUid || null,
            status: 'completed',
            completedAt: serverTimestamp(),
            testCompleted: true,
          });
          
          log(`  ${match.p1.displayName} defeats ${match.p2.displayName}`, 'info');
          results.push({ matchId: match.id, winnerId, loserId, hostUid, result: 'normal' });
          normalMatches++;
        }
      }
      
      // Brief pause to let triggers fire
      await new Promise(r => setTimeout(r, useFullLifecycle ? 300 : 400));
    }
    
    // Summary
    let summary = `Completed ${results.length} matches in Round ${round}`;
    if (useFullLifecycle) {
      summary += ` [Full lifecycle: ${lifecycleStages.locked}L->${lifecycleStages.ready || lifecycleStages.verified}V->${lifecycleStages.live}LIVE]`;
    }
    if (forfeitsSimulated > 0) summary += ` (${forfeitsSimulated} forfeits)`;
    if (bracketHolesCreated > 0) summary += ` (${bracketHolesCreated} bracket holes)`;
    log(summary, 'success');
    
    // Flood summary for this round
    if (floodEnabled && roundFloodContributions > 0) {
      const waveStr = Object.entries(roundWaves)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => `${count} ${type}s`)
        .join(', ');
      log(`  [FLOOD] Round total: ${roundFloodContributions} contributions, $${(roundFloodAmountCents / 100).toFixed(2)} (${waveStr})`, 'purple');
      if (roundMilestonesHit.length > 0) {
        log(`  [TSUNAMI] Milestones hit this round: ${roundMilestonesHit.map(m => '$' + (m/100).toLocaleString()).join(', ')}`, 'warning');
      }
    }
    
    return {
      results,
      flood: {
        contributions: roundFloodContributions,
        amountCents: roundFloodAmountCents,
        milestonesHit: roundMilestonesHit,
        waves: roundWaves,
      },
    };
  }, [log, simulateFullMatchLifecycle, simulateFloodDuringMatch]);
  
  // =========================================================================
  // STEP 6: VERIFY ADVANCEMENT
  // =========================================================================
  
  const verifyAdvancement = useCallback(async (tournamentId) => {
    log('Verifying team advancement...', 'info');
    
    // Wait longer for triggers to process (bracket holes need multiple trigger chains)
    await new Promise(r => setTimeout(r, 3000));
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const matchesSnap = await getDocs(collection(tournamentRef, 'matches'));
    const matches = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    setMatchesData(matches);
    
    const checks = [];
    
    // Check Round 1 outcomes
    const round1 = matches.filter(m => m.round === 1 && m.status === 'completed');
    const round2 = matches.filter(m => m.round === 2);
    
    // Track different match outcomes in R1
    const r1Normal = round1.filter(m => !m.forfeit && !m.isBracketHole);
    const r1Forfeits = round1.filter(m => m.forfeit && !m.isBracketHole);
    const r1BracketHoles = round1.filter(m => m.isBracketHole);
    
    if (r1Forfeits.length > 0 || r1BracketHoles.length > 0) {
      log(`  R1 Outcomes: ${r1Normal.length} normal, ${r1Forfeits.length} forfeits, ${r1BracketHoles.length} bracket holes`, 'info');
    }
    
    // Count how many R2 matches should have slots populated
    // Normal wins and forfeit wins advance, bracket holes propagate as holes
    const expectedAdvancers = r1Normal.length + r1Forfeits.length;
    const expectedR2Populated = Math.ceil(expectedAdvancers / 2);
    const actualR2P1 = round2.filter(m => m.p1?.pickupGroupId && !m.p1?.isBracketHole).length;
    const r2BracketHoleSlots = round2.filter(m => m.p1?.isBracketHole || m.p2?.isBracketHole).length;
    
    checks.push({
      label: 'R2 matches with teams',
      expected: `>= ${expectedR2Populated - 1}`,
      actual: `${actualR2P1} populated${r2BracketHoleSlots > 0 ? `, ${r2BracketHoleSlots} with hole slots` : ''}`,
      pass: actualR2P1 >= expectedR2Populated - 1,
    });
    
    // Check winners advanced with correct pickupGroupId (skip bracket holes)
    for (const r1Match of round1.filter(m => m.winnerPickupGroupId).slice(0, 2)) {
      if (!r1Match.nextMatchId) continue;
      
      const nextMatch = matches.find(m => m.id === r1Match.nextMatchId);
      if (!nextMatch) continue;
      
      const advancedCorrectly = 
        nextMatch.p1?.pickupGroupId === r1Match.winnerPickupGroupId ||
        nextMatch.p2?.pickupGroupId === r1Match.winnerPickupGroupId;
      
      const matchType = r1Match.forfeit ? ' (forfeit win)' : '';
      checks.push({
        label: `Winner of ${r1Match.id}${matchType} advanced`,
        expected: r1Match.winnerPickupGroupId?.slice(-8) || 'N/A',
        actual: advancedCorrectly ? 'Yes' : 'No',
        pass: advancedCorrectly,
      });
    }
    
    // Check bracket hole propagation
    for (const r1Hole of r1BracketHoles.slice(0, 2)) {
      if (!r1Hole.nextMatchId) continue;
      
      const nextMatch = matches.find(m => m.id === r1Hole.nextMatchId);
      if (!nextMatch) continue;
      
      const slot = r1Hole.nextMatchSlot || 'p1';
      const slotIsBracketHole = nextMatch[slot]?.isBracketHole === true;
      
      checks.push({
        label: `Bracket hole from ${r1Hole.id} propagated`,
        expected: `${slot} is bracket hole`,
        actual: slotIsBracketHole ? 'Yes' : `No (slot data: ${JSON.stringify(nextMatch[slot]?.displayName || 'empty')})`,
        pass: slotIsBracketHole,
      });
      
      // Check if opponent auto-advanced (if they're already in the match)
      const otherSlot = slot === 'p1' ? 'p2' : 'p1';
      const opponentExists = nextMatch[otherSlot]?.pickupGroupId && !nextMatch[otherSlot]?.isBracketHole;
      
      if (opponentExists && nextMatch.status === 'completed' && nextMatch.completedReason === 'opponent_bracket_hole') {
        checks.push({
          label: `Auto-advance for ${r1Hole.nextMatchId}`,
          expected: 'opponent_bracket_hole',
          actual: nextMatch.completedReason,
          pass: true,
        });
      }
    }
    
    // Count auto-advances across all rounds
    const autoAdvances = matches.filter(m => m.completedReason === 'opponent_bracket_hole');
    if (autoAdvances.length > 0 || r1BracketHoles.length > 0) {
      checks.push({
        label: 'Auto-advances from bracket holes',
        expected: `~${r1BracketHoles.length}`,
        actual: `${autoAdvances.length}`,
        pass: autoAdvances.length >= r1BracketHoles.length * 0.5, // At least half should auto-advance
      });
    }
    
    // =========================================================================
    // LIFECYCLE VERIFICATION - Check matches went through proper states
    // =========================================================================
    
    // Check R1 completed matches have proper lifecycle data
    const r1Completed = round1.filter(m => m.status === 'completed' && !m.isBracketHole && !m.forfeit);
    
    if (r1Completed.length > 0) {
      // Check for playersReadyState (proves ready-up was simulated)
      const hasReadyState = r1Completed.filter(m => m.playersReadyState && Object.keys(m.playersReadyState).length > 0);
      checks.push({
        label: 'R1 matches have playersReadyState',
        expected: `${r1Completed.length} matches`,
        actual: `${hasReadyState.length} matches`,
        pass: hasReadyState.length >= r1Completed.length * 0.9, // Allow 10% tolerance
      });
      
      // Check for readyStatus field
      const hasReadyStatus = r1Completed.filter(m => m.readyStatus);
      checks.push({
        label: 'R1 matches have readyStatus',
        expected: `${r1Completed.length} matches`,
        actual: `${hasReadyStatus.length} matches`,
        pass: hasReadyStatus.length >= r1Completed.length * 0.9,
      });
      
      // Check for verification data
      const hasVerification = r1Completed.filter(m => m.verificationResults || m.verificationSkipped);
      checks.push({
        label: 'R1 matches have verification data',
        expected: `${r1Completed.length} matches`,
        actual: `${hasVerification.length} matches`,
        pass: hasVerification.length >= r1Completed.length * 0.9,
      });
      
      // Check for liveAt timestamp (proves match went live)
      const wentLive = r1Completed.filter(m => m.liveAt);
      checks.push({
        label: 'R1 matches went LIVE',
        expected: `${r1Completed.length} matches`,
        actual: `${wentLive.length} matches`,
        pass: wentLive.length >= r1Completed.length * 0.9,
      });
      
      // Sample one match for detailed lifecycle check
      const sampleMatch = r1Completed[0];
      const samplePlayers = sampleMatch.playersReadyState ? Object.keys(sampleMatch.playersReadyState).length : 0;
      const sampleReadyCount = sampleMatch.playersReady || 0;
      
      checks.push({
        label: 'Sample match player count',
        expected: `${teamSize * 2} players`,
        actual: `${samplePlayers} players, ${sampleReadyCount} ready`,
        pass: samplePlayers === teamSize * 2 && sampleReadyCount === teamSize * 2,
      });
      
      // Log lifecycle flow for sample match
      log(`  [LIFECYCLE] Sample match ${sampleMatch.id}:`, 'info');
      log(`    readyStatus: ${sampleMatch.readyStatus}`, 'info');
      log(`    playersReady: ${sampleMatch.playersReady}/${sampleMatch.totalPlayersRequired}`, 'info');
      log(`    verificationPassed: ${sampleMatch.verificationPassed}`, 'info');
      log(`    liveAt: ${sampleMatch.liveAt ? 'Yes' : 'No'}`, 'info');
    }
    
    // Check rosters stayed together
    const r2WithBoth = round2.filter(m => m.p1?.roster && m.p2?.roster && !m.p1?.isBracketHole && !m.p2?.isBracketHole);
    if (r2WithBoth.length > 0) {
      const match = r2WithBoth[0];
      checks.push({
        label: 'R2 P1 roster size',
        expected: teamSize,
        actual: match.p1.roster.length,
        pass: match.p1.roster.length === teamSize,
      });
    }
    
    setVerifications(prev => [...prev, { title: 'Team Advancement & Lifecycle', checks }]);
    
    const allPass = checks.every(c => c.pass);
    if (allPass) {
      log('Advancement verification PASSED', 'success');
    } else {
      const failed = checks.filter(c => !c.pass);
      log(`Advancement verification FAILED: ${failed.map(f => f.label).join(', ')}`, 'error');
    }
    
    return { allPass, checks };
  }, [teamSize, log]);
  
  // =========================================================================
  // STEP 8: COMPLETE TOURNAMENT (Run all remaining rounds through finals)
  // =========================================================================
  
  const completeTournament = useCallback(async (tournamentId, options = {}) => {
    log('Running all remaining rounds through finals...', 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    const config = SCENARIOS[scenario];
    const { 
      forfeitChance = config.forfeitChance || 0, 
      forfeitType = config.forfeitType || 'single',
      useFullLifecycle = true,
      floodEnabled = config.floodEnabled || false,
      floodViewerCount = config.floodViewers || 0,
      floodIntensity = config.floodIntensity || 'normal',
    } = options;
    
    // Calculate total rounds needed
    const bracketSize = config.expectedBracket;
    const totalRounds = Math.ceil(Math.log2(bracketSize));
    
    log(`  Bracket size: ${bracketSize}, Total rounds: ${totalRounds}`, 'info');
    log(`  Mode: ${useFullLifecycle ? 'FULL LIFECYCLE (locked->ready->verified->live->completed)' : 'QUICK MODE'}`, 'info');
    if (forfeitChance > 0) {
      log(`  Forfeit simulation: ${Math.round(forfeitChance * 100)}% chance, type: ${forfeitType}`, 'info');
    }
    
    // ===== FLOOD SETUP =====
    let viewers = [];
    if (floodEnabled && floodViewerCount > 0) {
      log(`  Flood the Pool: ENABLED (${floodViewerCount} viewers, ${floodIntensity} intensity)`, 'purple');
      viewers = await createViewerUsers(floodViewerCount, tournamentId);
    }
    
    let totalForfeits = 0;
    let totalBracketHoles = 0;
    let totalLifecycleStats = {
      matchesProcessed: 0,
      readyUpsSimulated: 0,
      verificationsSimulated: 0,
      liveMatches: 0,
    };
    
    // Flood tracking across all rounds
    let totalFloodStats = {
      contributions: 0,
      amountCents: 0,
      milestonesHit: [],
      waves: { ripple: 0, wave: 0, surge: 0, tsunami: 0 },
      failed: false,
      errorMessage: null,
    };
    
    // Start from round 2 (round 1 already done)
    for (let round = 2; round <= totalRounds; round++) {
      log(`  Simulating Round ${round}...`, 'info');
      
      // Wait longer for previous round triggers to complete (bracket holes need propagation time)
      await new Promise(r => setTimeout(r, 2500));
      
      const matchesSnap = await getDocs(
        query(
          collection(tournamentRef, 'matches'),
          where('round', '==', round)
        )
      );
      
      const allRoundMatches = matchesSnap.docs.map(d => ({ 
        id: d.id, 
        ref: doc(tournamentRef, 'matches', d.id),
        ...d.data() 
      }));
      
      // Debug: Log all matches in this round
      const completedCount = allRoundMatches.filter(m => m.status === 'completed').length;
      const pendingWithBoth = allRoundMatches.filter(m => 
        m.status !== 'completed' && 
        m.p1?.pickupGroupId && 
        m.p2?.pickupGroupId && 
        !m.p1.isBracketHole && 
        !m.p2.isBracketHole
      ).length;
      const pendingWithHole = allRoundMatches.filter(m =>
        m.status !== 'completed' &&
        (m.p1?.isBracketHole || m.p2?.isBracketHole)
      ).length;
      const autoAdvanced = allRoundMatches.filter(m => m.completedReason === 'opponent_bracket_hole').length;
      
      if (round > 2) {
        log(`    [R${round}] Status: ${completedCount} completed (${autoAdvanced} auto-advanced), ${pendingWithBoth} ready, ${pendingWithHole} with holes`, 'info');
      }
      
      // Filter to matches that need simulation (not completed, both sides populated, no bracket holes)
      const roundMatches = allRoundMatches.filter(m => 
        m.status !== 'completed' && 
        m.p1 && m.p2 && 
        !m.p1.isBye && !m.p2.isBye &&
        !m.p1.isBracketHole && !m.p2.isBracketHole
      );
      
      if (roundMatches.length === 0) {
        log(`    No pending matches in Round ${round}`, 'warning');
        continue;
      }
      
      for (const match of roundMatches) {
        const matchRef = match.ref;
        const roundName = round === totalRounds ? 'FINALS' : `R${round}`;
        const isFinals = round === totalRounds;
        const isSemifinals = round === totalRounds - 1;
        
        // Determine match outcome
        // PROTECTION: Never create bracket holes in finals or semifinals
        // Reduce forfeit chance in later rounds (tournament gets more serious)
        let effectiveForfeitChance = forfeitChance;
        if (isFinals) {
          effectiveForfeitChance = 0; // No forfeits in finals - both teams must play
        } else if (isSemifinals) {
          effectiveForfeitChance = forfeitChance * 0.3; // 30% of normal forfeit rate
        } else if (round >= totalRounds - 2) {
          effectiveForfeitChance = forfeitChance * 0.5; // 50% of normal forfeit rate
        }
        
        const shouldForfeit = effectiveForfeitChance > 0 && Math.random() < effectiveForfeitChance;
        // NEVER allow bracket holes in semifinals or finals
        const isBracketHole = shouldForfeit && !isFinals && !isSemifinals && 
          (forfeitType === 'both' || (forfeitType === 'mixed' && Math.random() < 0.3));
        
        // Debug: Log the full match structure
        log(`    [DEBUG] Match ${match.id}:`, 'info');
        log(`      p1.pickupGroupId: ${match.p1?.pickupGroupId}`, 'info');
        log(`      p1.roster: ${JSON.stringify(match.p1?.roster)}`, 'info');
        log(`      p1.displayName: ${match.p1?.displayName}`, 'info');
        log(`      participantUids: ${JSON.stringify(match.participantUids?.slice(0,3))}...`, 'info');
        
        if (useFullLifecycle) {
          // ===== FULL LIFECYCLE SIMULATION =====
          let winner = 'p1'; // Default: P1 wins
          if (isBracketHole) {
            winner = 'both_no_show';
          } else if (shouldForfeit) {
            winner = 'forfeit_p2';
          }
          
          const lifecycleResult = await simulateFullMatchLifecycle(matchRef, match, tournamentId, {
            winner,
            skipReadyUp: false,
            skipVerification: false,
            logFn: (msg, type) => log(`      ${msg}`, type),
          });
          
          totalLifecycleStats.matchesProcessed++;
          if (lifecycleResult.lifecycle.includes('all_ready')) totalLifecycleStats.readyUpsSimulated++;
          if (lifecycleResult.lifecycle.includes('verified')) totalLifecycleStats.verificationsSimulated++;
          if (lifecycleResult.lifecycle.includes('live')) totalLifecycleStats.liveMatches++;
          
          // ===== FLOOD SIMULATION DURING LIVE MATCH =====
          if (floodEnabled && viewers.length > 0 && lifecycleResult.outcome === 'normal' && !totalFloodStats.failed) {
            const floodResult = await simulateFloodDuringMatch(
              tournamentId,
              match.id,
              viewers,
              floodIntensity
            );
            
            // Check for permission error on first contribution
            if (floodResult.error) {
              log(`      [FLOOD ERROR] ${floodResult.error}`, 'error');
              log(`      [FLOOD] Disabling flood simulation for remaining matches`, 'warning');
              totalFloodStats.failed = true;
              totalFloodStats.errorMessage = floodResult.error;
            } else {
              totalFloodStats.contributions += floodResult.contributions;
              totalFloodStats.amountCents += floodResult.totalCents;
              totalFloodStats.milestonesHit.push(...(floodResult.milestonesHit || []));
              
              // Merge wave counts
              for (const [type, count] of Object.entries(floodResult.waves || {})) {
                totalFloodStats.waves[type] = (totalFloodStats.waves[type] || 0) + count;
              }
              
              if (floodResult.contributions > 0) {
                log(`      [FLOOD] ${floodResult.contributions} contributions (+$${(floodResult.totalCents / 100).toFixed(2)})`, 'purple');
              }
              if (floodResult.milestonesHit?.length > 0) {
                for (const milestone of floodResult.milestonesHit) {
                  log(`      [TSUNAMI] Milestone hit: $${(milestone / 100).toLocaleString()}!`, 'warning');
                }
              }
            }
          }
          
          if (lifecycleResult.outcome === 'bracket_hole') {
            log(`    [${roundName}] [BRACKET HOLE] ${match.p1.displayName} vs ${match.p2.displayName}`, 'warning');
            totalBracketHoles++;
          } else if (lifecycleResult.outcome === 'forfeit') {
            log(`    [${roundName}] [FORFEIT] ${lifecycleResult.winner} wins - opponent no-show`, 'warning');
            totalForfeits++;
          } else {
            log(`    [${roundName}] ${match.p1.displayName || 'P1'} defeats ${match.p2.displayName || 'P2'}`, 'info');
          }
          
        } else {
          // ===== QUICK MODE (legacy) =====
          if (isBracketHole) {
            await updateDoc(matchRef, {
              winnerId: null,
              winnerPickupGroupId: null,
              loserPickupGroupId: null,
              status: 'completed',
              completedReason: 'both_no_show',
              isBracketHole: true,
              forfeit: true,
              completedAt: serverTimestamp(),
              testCompleted: true,
            });
            
            log(`    [${roundName}] [BRACKET HOLE] ${match.p1.displayName} vs ${match.p2.displayName}`, 'warning');
            totalBracketHoles++;
            
          } else if (shouldForfeit) {
            const winnerId = match.p1.pickupGroupId;
            const winnerUserId = match.p1.roster?.[0] || match.p1.captainId || winnerId;
            const hostUid = match.hostUid || winnerUserId;
            
            await updateDoc(matchRef, {
              winnerId: winnerUserId || null,
              winnerPickupGroupId: winnerId || null,
              loserPickupGroupId: match.p2?.pickupGroupId || null,
              hostUid: hostUid || null,
              status: 'completed',
              completedReason: 'forfeit_no_show',
              forfeit: true,
              forfeitTeamId: match.p2?.pickupGroupId,
              completedAt: serverTimestamp(),
              testCompleted: true,
            });
            
            log(`    [${roundName}] [FORFEIT] ${match.p1.displayName || 'P1'} wins - ${match.p2.displayName || 'P2'} no-show`, 'warning');
            totalForfeits++;
            
          } else {
            const winnerId = match.p1.pickupGroupId;
            const winnerUserId = match.p1.roster?.[0] || 
                                 match.p1.captainId || 
                                 match.p1.rosterUids?.[0] ||
                                 match.participantUids?.[0];
            
            if (!winnerUserId) {
              log(`    [WARN] Could not find winnerUserId - using pickupGroupId as fallback`, 'warning');
            }
            
            const finalWinnerId = winnerUserId || winnerId;
            const hostUid = match.hostUid || finalWinnerId;
            
            await updateDoc(matchRef, {
              winnerId: finalWinnerId || null,
              winnerPickupGroupId: winnerId || null,
              loserPickupGroupId: match.p2?.pickupGroupId || null,
              hostUid: hostUid || null,
              status: 'completed',
              completedAt: serverTimestamp(),
              testCompleted: true,
            });
            
            log(`    [${roundName}] ${match.p1.displayName || 'P1'} defeats ${match.p2.displayName || 'P2'}`, 'info');
          }
        }
        
        // Brief pause for triggers
        await new Promise(r => setTimeout(r, useFullLifecycle ? 300 : 400));
      }
    }
    
    // Log lifecycle summary
    if (useFullLifecycle) {
      log(`  Lifecycle summary: ${totalLifecycleStats.matchesProcessed} matches processed`, 'info');
      log(`    Ready-ups: ${totalLifecycleStats.readyUpsSimulated}, Verifications: ${totalLifecycleStats.verificationsSimulated}, Live: ${totalLifecycleStats.liveMatches}`, 'info');
    }
    
    // Log forfeit summary
    if (totalForfeits > 0 || totalBracketHoles > 0) {
      log(`  Forfeit summary: ${totalForfeits} forfeits, ${totalBracketHoles} bracket holes`, 'info');
    }
    
    // Log flood summary
    if (floodEnabled) {
      if (totalFloodStats.failed) {
        log(`  ========================================`, 'error');
        log(`  FLOOD THE POOL FAILED`, 'error');
        log(`  ========================================`, 'error');
        log(`  Error: ${totalFloodStats.errorMessage}`, 'error');
        log(`  This is likely a Firestore permissions issue.`, 'warning');
        log(`  The tournament document may not allow flood writes from client.`, 'warning');
        log(`  ========================================`, 'error');
      } else if (totalFloodStats.contributions > 0) {
        log(`  ========================================`, 'purple');
        log(`  FLOOD THE POOL SUMMARY`, 'purple');
        log(`  ========================================`, 'purple');
        log(`  Total contributions: ${totalFloodStats.contributions}`, 'purple');
        log(`  Total flooded: $${(totalFloodStats.amountCents / 100).toFixed(2)}`, 'purple');
        
        const waveStr = Object.entries(totalFloodStats.waves)
          .filter(([_, count]) => count > 0)
          .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
          .join(', ');
        log(`  Wave breakdown: ${waveStr}`, 'purple');
        
        if (totalFloodStats.milestonesHit.length > 0) {
          const uniqueMilestones = [...new Set(totalFloodStats.milestonesHit)].sort((a, b) => a - b);
          log(`  TSUNAMI milestones hit: ${uniqueMilestones.map(m => '$' + (m/100).toLocaleString()).join(', ')}`, 'warning');
        }
        log(`  ========================================`, 'purple');
        
        // Update flood stats state
        setFloodStats(totalFloodStats);
      } else {
        log(`  [FLOOD] No contributions recorded (viewers may have run out of balance)`, 'warning');
      }
    }
    
    // Wait for tournament completion trigger
    log('  Waiting for tournament completion trigger...', 'info');
    await new Promise(r => setTimeout(r, 3000));
    
    // Check tournament status
    const tournamentSnap = await getDoc(tournamentRef);
    const tournamentData = tournamentSnap.data();
    
    if (tournamentData.status === 'completed') {
      log('Tournament completed successfully!', 'success');
      if (tournamentData.isTeamTournament) {
        log(`  Champion Team: ${tournamentData.championTeamName}`, 'info');
        log(`  Champion Roster: ${tournamentData.championRosterUids?.join(', ')}`, 'info');
      } else {
        log(`  Champion: ${tournamentData.championId}`, 'info');
      }
      return { success: true, tournamentData, totalForfeits, totalBracketHoles, floodStats: totalFloodStats };
    } else {
      log(`Tournament status: ${tournamentData.status} (expected: completed)`, 'warning');
      return { success: false, tournamentData, status: tournamentData.status, totalForfeits, totalBracketHoles, floodStats: totalFloodStats };
    }
  }, [scenario, log, simulateFullMatchLifecycle, createViewerUsers, simulateFloodDuringMatch]);
  
  // =========================================================================
  // STEP 9: VERIFY PAYOUTS
  // =========================================================================
  
  const verifyPayouts = useCallback(async (tournamentId) => {
    log('Verifying payout distribution...', 'info');
    
    const tournamentRef = doc(db, 'tournaments', tournamentId);
    
    // Dynamic timeout based on bracket size (larger brackets = more wallet updates = longer wait)
    // 512 teams: 128 places Ã— 6 members = 768 wallet updates @ ~0.5s each = ~6-8 minutes
    // 256 teams: 64 places Ã— 6 members = 384 wallet updates @ ~0.5s each = ~3-4 minutes
    const scenarioConfig = SCENARIOS[scenario] || {};
    const bracketSize = scenarioConfig.expectedBracket || scenarioConfig.expectedTeams || 
                        scenarioConfig.fullTeams + (scenarioConfig.partials?.length || 0) || 32;
    
    // Scale timeout: small=30s, medium=60s, large=180s, huge=480s
    let maxAttempts, pollIntervalMs;
    if (bracketSize >= 512) {
      maxAttempts = 80;      // 80 Ã— 6s = 480s (8 minutes) for 512+ teams
      pollIntervalMs = 6000;
    } else if (bracketSize >= 256) {
      maxAttempts = 60;      // 60 Ã— 5s = 300s (5 minutes) for 256 teams
      pollIntervalMs = 5000;
    } else if (bracketSize >= 128) {
      maxAttempts = 45;      // 45 Ã— 4s = 180s (3 minutes) for 128 teams
      pollIntervalMs = 4000;
    } else if (bracketSize >= 64) {
      maxAttempts = 20;      // 20 Ã— 3s = 60s for 64 teams
      pollIntervalMs = 3000;
    } else {
      maxAttempts = 10;      // 10 Ã— 3s = 30s for small brackets
      pollIntervalMs = 3000;
    }
    
    log(`  Waiting for payouts (up to ${maxAttempts * pollIntervalMs / 1000}s for ${bracketSize}-team bracket)...`, 'info');
    
    // Poll for payout completion
    let tournament = null;
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      attempts++;
      log(`  Checking for payout completion (attempt ${attempts}/${maxAttempts})...`, 'info');
      await new Promise(r => setTimeout(r, pollIntervalMs));
      
      const tournamentSnap = await getDoc(tournamentRef);
      tournament = tournamentSnap.data();
      
      if (tournament.payoutsCompleted === true || tournament.financials?.prizePool > 0) {
        log(`  Payouts detected!`, 'success');
        break;
      }
    }
    
    if (!tournament) {
      const tournamentSnap = await getDoc(tournamentRef);
      tournament = tournamentSnap.data();
    }
    
    // Log the financials we're reading
    log(`  [DEBUG] tournament.financials: ${JSON.stringify(tournament.financials || {})}`, 'info');
    log(`  [DEBUG] tournament.payoutsCompleted: ${tournament.payoutsCompleted}`, 'info');
    
    // Get all matches for bracket hole analysis
    const matchesSnap = await getDocs(collection(tournamentRef, 'matches'));
    const allMatches = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    const checks = [];
    
    // ===== BRACKET HOLE & FORFEIT ANALYSIS =====
    const completedMatches = allMatches.filter(m => m.status === 'completed');
    const bracketHoles = completedMatches.filter(m => m.isBracketHole);
    const forfeits = completedMatches.filter(m => m.forfeit && !m.isBracketHole);
    const normalMatches = completedMatches.filter(m => !m.isBracketHole && !m.forfeit);
    const autoAdvances = completedMatches.filter(m => m.completedReason === 'opponent_bracket_hole');
    
    if (bracketHoles.length > 0 || forfeits.length > 0) {
      log(`  Match Outcomes:`, 'info');
      log(`    Normal: ${normalMatches.length}`, 'info');
      log(`    Forfeits: ${forfeits.length}`, 'info');
      log(`    Bracket Holes: ${bracketHoles.length}`, 'info');
      log(`    Auto-Advances: ${autoAdvances.length}`, 'info');
      
      checks.push({
        label: 'Bracket hole count',
        expected: 'Tracked',
        actual: `${bracketHoles.length} holes`,
        pass: true, // Just tracking, not a pass/fail
      });
      
      checks.push({
        label: 'Forfeit count',
        expected: 'Tracked',
        actual: `${forfeits.length} forfeits`,
        pass: true,
      });
      
      // Verify auto-advances happened correctly
      if (autoAdvances.length > 0) {
        // Each auto-advance should have a winner
        const validAutoAdvances = autoAdvances.filter(m => m.winnerId);
        checks.push({
          label: 'Auto-advances have winners',
          expected: `${autoAdvances.length} with winners`,
          actual: `${validAutoAdvances.length} with winners`,
          pass: validAutoAdvances.length === autoAdvances.length,
        });
      }
    }
    
    // Check 1: Tournament completed
    checks.push({
      label: 'Tournament status',
      expected: 'completed',
      actual: tournament.status,
      pass: tournament.status === 'completed',
    });
    
    // Check 2: Payouts marked complete
    checks.push({
      label: 'Payouts completed flag',
      expected: 'true',
      actual: String(tournament.payoutsCompleted || false),
      pass: tournament.payoutsCompleted === true,
    });
    
    // Check 3: Financials calculated
    const financials = tournament.financials || {};
    checks.push({
      label: 'Prize pool calculated',
      expected: '> 0 (or free event)',
      actual: financials.prizePool !== undefined ? `$${(financials.prizePool / 100).toFixed(2)}` : 'undefined',
      pass: financials.prizePool !== undefined,
    });
    
    // Check 4: Total paid out (may be less than prize pool if bracket holes or rounding)
    if (financials.prizePool > 0) {
      const paidOut = financials.totalPaidOut || 0;
      const diff = financials.prizePool - paidOut;
      const hasBracketHoleUnpaid = bracketHoles.length > 0 && diff > 0;
      
      // Allow tolerance for rounding in large tournaments
      // With 768 recipients (128 teams x 6), rounding can cause up to ~$1 difference
      const roundingTolerance = 100; // $1 in cents
      const isWithinTolerance = Math.abs(diff) <= roundingTolerance;
      
      checks.push({
        label: 'Total paid out',
        expected: hasBracketHoleUnpaid ? `<= $${(financials.prizePool / 100).toFixed(2)}` : `~$${(financials.prizePool / 100).toFixed(2)} (within $1)`,
        actual: `$${(paidOut / 100).toFixed(2)}${diff > 0 ? ` ($${(diff/100).toFixed(2)} rounding residual)` : ''}`,
        pass: hasBracketHoleUnpaid ? paidOut <= financials.prizePool : isWithinTolerance,
      });
    }
    
    // Check 5: Champion info saved
    const isTeam = tournament.isTeamTournament;
    if (isTeam) {
      checks.push({
        label: 'Champion team name',
        expected: 'Set',
        actual: tournament.championTeamName || 'undefined',
        pass: !!tournament.championTeamName,
      });
      
      checks.push({
        label: 'Champion roster UIDs',
        expected: '> 0 members',
        actual: `${tournament.championRosterUids?.length || 0} members`,
        pass: (tournament.championRosterUids?.length || 0) > 0,
      });
    } else {
      checks.push({
        label: 'Champion ID',
        expected: 'Set',
        actual: tournament.championId || 'undefined',
        pass: !!tournament.championId,
      });
    }
    
    // Check 6: Payout distribution recorded (for non-free events)
    if (financials.prizePool > 0) {
      const distribution = financials.payoutDistribution || [];
      const stageBands = financials.stageBands || [];
      
      // For band-based payouts, we expect more than just the winner
      const bracketSize = tournament.bracketSize || 8;
      const expectedMinRecipients = bracketSize >= 8 ? 2 : 1; // 8+ bracket pays at least 2 places
      
      checks.push({
        label: 'Payout distribution records',
        expected: `>= ${expectedMinRecipients} placements`,
        actual: `${distribution.length} entries`,
        pass: distribution.length >= expectedMinRecipients,
      });
      
      // Check if multiple placements were paid (for 8+ brackets)
      if (bracketSize >= 8) {
        const uniquePlacements = new Set(distribution.map(d => d.rank || 1));
        checks.push({
          label: 'Multiple placements paid',
          expected: '>= 2 ranks',
          actual: `${uniquePlacements.size} ranks (${[...uniquePlacements].join(', ')})`,
          pass: uniquePlacements.size >= 2,
        });
        
        // Log stage bands
        if (stageBands.length > 0) {
          log(`  Payout Bands:`, 'info');
          stageBands.forEach(band => {
            log(`    ${band.stage} (${band.minRank}-${band.maxRank}): ${band.percent}% = $${((band.totalCents || 0) / 100).toFixed(2)}`, 'info');
          });
        }
      }
      
      // For team events, verify champion members received correct percentage
      if (isTeam && tournament.championRosterUids?.length > 0) {
        const memberCount = tournament.championRosterUids.length;
        
        // Get actual champion percentage from stageBands
        const championBand = stageBands.find(b => b.minRank === 1 || b.stage === 'Champion');
        const championPercent = championBand?.percent || (bracketSize >= 64 ? 28 : bracketSize >= 32 ? 40 : bracketSize >= 16 ? 50 : bracketSize >= 8 ? 65 : 100);
        const championPerPlace = championBand?.perPlaceCents || Math.floor(financials.prizePool * championPercent / 100);
        const expectedPerMember = Math.floor(championPerPlace / memberCount);
        
        checks.push({
          label: `Champion payout (${championPercent}% of pool)`,
          expected: `~$${(expectedPerMember / 100).toFixed(2)}/member`,
          actual: distribution.length > 0 ? `$${((distribution[0]?.amount || 0) / 100).toFixed(2)}/member` : 'N/A',
          pass: distribution.length > 0 && Math.abs((distribution[0]?.amount || 0) - expectedPerMember) < 100, // Allow $1 variance
        });
        
        // Check 7: Verify actual user balances (sample first winner)
        try {
          const firstWinnerId = tournament.championRosterUids[0];
          const userSnap = await getDoc(doc(db, 'users', firstWinnerId));
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const actualBalance = userData.balanceCents || 0;
            checks.push({
              label: 'Champion wallet credited',
              expected: `>= $${(expectedPerMember / 100).toFixed(2)}`,
              actual: `$${(actualBalance / 100).toFixed(2)}`,
              pass: actualBalance >= expectedPerMember * 0.9, // Allow 10% variance
            });
            log(`  First champion balance: $${(actualBalance / 100).toFixed(2)}`, 'info');
          } else {
            checks.push({
              label: 'Champion wallet credited',
              expected: 'User doc created',
              actual: 'User not found (payout may have failed)',
              pass: false,
            });
          }
        } catch (e) {
          log(`  [INFO] Could not verify user balance: ${e.message}`, 'info');
        }
        
        // Check runner-up payout for 8+ brackets
        if (bracketSize >= 8) {
          const runnerUpEntries = distribution.filter(d => d.rank === 2);
          
          // Get actual runner-up percentage from stageBands instead of hardcoding
          const runnerUpBand = financials.stageBands?.find(b => b.minRank === 2 || b.stage === 'Runner-up');
          const runnerUpPercent = runnerUpBand?.percent || (bracketSize >= 32 ? 25 : bracketSize >= 16 ? 30 : 35);
          const runnerUpPerPlace = runnerUpBand?.perPlaceCents || Math.floor(financials.prizePool * runnerUpPercent / 100);
          
          if (runnerUpEntries.length > 0) {
            const expectedPerMember = Math.floor(runnerUpPerPlace / teamSize);
            const actualPerMember = runnerUpEntries[0]?.amount || 0;
            
            checks.push({
              label: `Runner-up payout (${runnerUpPercent}% of pool)`,
              expected: `~$${(expectedPerMember / 100).toFixed(2)}/member`,
              actual: `$${(actualPerMember / 100).toFixed(2)}/member`,
              pass: Math.abs(actualPerMember - expectedPerMember) < 100, // Allow $1 variance
            });
          } else {
            checks.push({
              label: 'Runner-up payout',
              expected: 'Entries found',
              actual: 'No runner-up entries',
              pass: false,
            });
          }
        }
      }
    }
    
    // =========================================================================
    // HOST REWARD VERIFICATION
    // =========================================================================
    
    // Check host rewards for completed matches (reuse completedMatches from above)
    const matchesWithHost = completedMatches.filter(m => m.hostUid);
    const matchesWithReward = completedMatches.filter(m => m.hostRewardProcessed);
    
    if (matchesWithHost.length > 0) {
      checks.push({
        label: 'Host rewards processed',
        expected: `${matchesWithHost.length} matches`,
        actual: `${matchesWithReward.length} rewards`,
        pass: matchesWithReward.length >= matchesWithHost.length * 0.9, // Allow 90% (some may be processing)
      });
      
      // Check total host rewards paid
      const totalHostRewards = completedMatches.reduce((sum, m) => sum + (m.hostRewardCents || 0), 0);
      const totalHostPoints = completedMatches.reduce((sum, m) => sum + (m.hostRewardPoints || 0), 0);
      
      // Log regardless of cash amount
      log(`  Host Rewards: $${(totalHostRewards / 100).toFixed(2)} cash + ${totalHostPoints} pts across ${matchesWithReward.length} matches`, 'info');
      
      // For low-fee events ($1), cash rewards may be 0 but points should still be awarded
      const entryFee = tournament.entryFeeCents || 0;
      const isLowFeeEvent = entryFee <= 100; // $1 or less
      
      if (isLowFeeEvent) {
        // Low-fee events: check points instead of cash
        checks.push({
          label: 'Host points awarded (low-fee event)',
          expected: `>= ${matchesWithReward.length} pts`,
          actual: `${totalHostPoints} pts`,
          pass: totalHostPoints >= matchesWithReward.length,
        });
      } else if (totalHostRewards > 0) {
        checks.push({
          label: 'Host reward amount',
          expected: '> $0.00',
          actual: `$${(totalHostRewards / 100).toFixed(2)}`,
          pass: totalHostRewards > 0,
        });
      }
      
      // Check first host's stats (points should always be awarded)
      if (matchesWithReward.length > 0) {
        const firstHostId = matchesWithReward[0].hostUid;
        
        // Check hosts collection for points
        try {
          const hostsSnap = await getDoc(doc(db, 'hosts', firstHostId));
          if (hostsSnap.exists()) {
            const hostData = hostsSnap.data();
            const hostPoints = hostData.hostPoints || 0;
            const matchesHosted = hostData.hostStats?.totalMatchesHosted || 0;
            
            checks.push({
              label: 'Host stats updated',
              expected: '> 0 points',
              actual: `${hostPoints} pts, ${matchesHosted} matches`,
              pass: hostPoints > 0,
            });
            log(`  Host Stats: ${hostPoints} pts, ${matchesHosted} matches for ${firstHostId.slice(-8)}`, 'info');
          } else {
            log(`  [INFO] No hosts doc found for ${firstHostId.slice(-8)}`, 'info');
          }
        } catch (e) {
          log(`  [INFO] Could not verify host stats: ${e.message}`, 'info');
        }
        
        // Check escrow only for non-low-fee events
        if (!isLowFeeEvent) {
          try {
            const escrowSnap = await getDoc(doc(db, 'hostEscrow', firstHostId));
            if (escrowSnap.exists()) {
              const escrowData = escrowSnap.data();
              const pendingCents = escrowData.pendingCents || 0;
              
              checks.push({
                label: 'Host escrow credited',
                expected: '> $0.00',
                actual: `$${(pendingCents / 100).toFixed(2)}`,
                pass: pendingCents > 0,
              });
              log(`  Host Escrow: $${(pendingCents / 100).toFixed(2)} pending for ${firstHostId.slice(-8)}`, 'info');
            }
          } catch (e) {
            log(`  [INFO] Could not verify host escrow: ${e.message}`, 'info');
          }
        }
      }
    } else {
      log(`  [INFO] No hosts assigned to matches (host rewards skipped)`, 'info');
    }
    
    // Log summary
    log(`  Tournament: ${tournament.name}`, 'info');
    log(`  Entry Fee: $${((tournament.entryFeeCents || 0) / 100).toFixed(2)}`, 'info');
    log(`  Entrants: ${tournament.entrantsCount || 0}`, 'info');
    log(`  Prize Pool: $${((financials.prizePool || 0) / 100).toFixed(2)}`, 'info');
    log(`  Platform Fee: $${((financials.platformFee || 0) / 100).toFixed(2)}`, 'info');
    log(`  Payout Structure: ${financials.payoutStructureId || tournament.payoutStructureId || 'default'}`, 'info');
    
    // =========================================================================
    // FLOOD THE POOL VERIFICATION
    // =========================================================================
    
    const floodTotalCents = tournament.floodTotalCents || 0;
    const floodCount = tournament.floodCount || 0;
    
    if (floodTotalCents > 0 || floodCount > 0) {
      log(`  ========================================`, 'purple');
      log(`  FLOOD THE POOL VERIFICATION`, 'purple');
      log(`  ========================================`, 'purple');
      log(`  Total Flooded: $${(floodTotalCents / 100).toFixed(2)}`, 'purple');
      log(`  Contributions: ${floodCount}`, 'purple');
      
      checks.push({
        label: 'Flood total recorded',
        expected: '> $0.00',
        actual: `$${(floodTotalCents / 100).toFixed(2)}`,
        pass: floodTotalCents > 0,
      });
      
      checks.push({
        label: 'Flood contribution count',
        expected: '> 0',
        actual: `${floodCount} contributions`,
        pass: floodCount > 0,
      });
      
      // Check if flood was distributed
      const floodDistributed = tournament.floodDistributed === true;
      checks.push({
        label: 'Flood distribution completed',
        expected: 'true',
        actual: String(floodDistributed),
        pass: floodDistributed,
      });
      
      if (floodDistributed) {
        log(`  Flood Distributed: YES`, 'success');
      } else {
        log(`  Flood Distributed: NO (may still be processing)`, 'warning');
      }
      
      // Check flood contributions collection
      try {
        const floodContribsSnap = await getDocs(collection(tournamentRef, 'floodContributions'));
        const floodContribs = floodContribsSnap.docs.map(d => d.data());
        
        // Count wave types
        const waveTypes = { ripple: 0, wave: 0, surge: 0, tsunami: 0 };
        let tsunamiMilestones = [];
        
        for (const contrib of floodContribs) {
          if (contrib.waveType) {
            waveTypes[contrib.waveType] = (waveTypes[contrib.waveType] || 0) + 1;
          }
          if (contrib.tsunamiTriggered && contrib.milestoneReached) {
            tsunamiMilestones.push(contrib.milestoneReached);
          }
        }
        
        const waveStr = Object.entries(waveTypes)
          .filter(([_, count]) => count > 0)
          .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
          .join(', ');
        
        log(`  Wave Breakdown: ${waveStr}`, 'purple');
        
        if (tsunamiMilestones.length > 0) {
          const uniqueMilestones = [...new Set(tsunamiMilestones)].sort((a, b) => a - b);
          log(`  TSUNAMI Milestones Hit: ${uniqueMilestones.map(m => '$' + (m/100).toLocaleString()).join(', ')}`, 'warning');
          
          checks.push({
            label: 'Tsunami milestones triggered',
            expected: '>= 1',
            actual: `${uniqueMilestones.length} milestones`,
            pass: uniqueMilestones.length > 0,
          });
        }
        
        // Check if champion received flood payout
        if (tournament.championRosterUids?.length > 0) {
          const championId = tournament.championRosterUids[0];
          try {
            const championTxSnap = await getDocs(
              query(
                collection(db, 'users', championId, 'transactions'),
                where('type', '==', 'flood_payout')
              )
            );
            
            const floodPayoutTxs = championTxSnap.docs.map(d => d.data());
            const totalFloodPayout = floodPayoutTxs.reduce((sum, tx) => sum + (tx.amountCents || 0), 0);
            
            if (floodPayoutTxs.length > 0) {
              log(`  Champion Flood Payout: $${(totalFloodPayout / 100).toFixed(2)}`, 'success');
              
              checks.push({
                label: 'Champion received flood payout',
                expected: '> $0.00',
                actual: `$${(totalFloodPayout / 100).toFixed(2)}`,
                pass: totalFloodPayout > 0,
              });
            } else {
              log(`  Champion Flood Payout: Pending or not yet processed`, 'warning');
            }
          } catch (e) {
            log(`  [INFO] Could not verify champion flood payout: ${e.message}`, 'info');
          }
        }
      } catch (e) {
        log(`  [INFO] Could not verify flood contributions: ${e.message}`, 'info');
      }
      
      log(`  ========================================`, 'purple');
    }
    
    if (isTeam && tournament.championRosterUids?.length > 0) {
      // Use actual stageBands from financials
      const stageBands = financials.stageBands || [];
      const championBand = stageBands.find(b => b.minRank === 1 || b.stage === 'Champion');
      const runnerUpBand = stageBands.find(b => b.minRank === 2 || b.stage === 'Runner-up');
      
      const bracketSize = tournament.bracketSize || 8;
      const championPercent = championBand?.percent || (bracketSize >= 32 ? 40 : bracketSize >= 16 ? 50 : bracketSize >= 8 ? 65 : 100);
      const championPool = championBand?.totalCents || Math.floor((financials.prizePool || 0) * championPercent / 100);
      const perMember = Math.floor(championPool / tournament.championRosterUids.length);
      
      log(`  Champion (${championPercent}%): $${(championPool / 100).toFixed(2)} ($${(perMember / 100).toFixed(2)}/member)`, 'info');
      
      if (runnerUpBand || bracketSize >= 8) {
        const runnerUpPercent = runnerUpBand?.percent || (bracketSize >= 32 ? 25 : bracketSize >= 16 ? 30 : 35);
        const runnerUpPool = runnerUpBand?.totalCents || Math.floor((financials.prizePool || 0) * runnerUpPercent / 100);
        log(`  Runner-up (${runnerUpPercent}%): $${(runnerUpPool / 100).toFixed(2)}`, 'info');
      }
    }
    
    setVerifications(prev => [...prev, { title: 'Payout Verification', checks }]);
    
    const allPass = checks.every(c => c.pass);
    if (allPass) {
      log('Payout verification PASSED', 'success');
    } else {
      const failed = checks.filter(c => !c.pass);
      log(`Payout verification FAILED: ${failed.map(f => f.label).join(', ')}`, 'error');
    }
    
    return { allPass, checks, financials, tournament };
  }, [log, scenario]);
  
  // =========================================================================
  // RUN FULL TEST
  // =========================================================================
  
  const runFullTest = useCallback(async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setLoading(true);
    clearAll();
    setVerifications([]);
    
    log('='.repeat(50), 'info');
    log('STARTING END-TO-END TEAM TOURNAMENT TEST', 'info');
    log('='.repeat(50), 'info');
    
    try {
      // Step 1: Create
      setCurrentStep(0);
      const tournamentId = await createTournament();
      setStepResult('create', 'success', `ID: ${tournamentId.slice(-12)}`);
      
      // Step 2: Entries
      setCurrentStep(1);
      const entries = await addEntries(tournamentId);
      setStepResult('entries', 'success', `${entries.length} entries`);
      
      // Step 3: Lock (triggers pickup matching)
      setCurrentStep(2);
      const scenarioConfig = SCENARIOS[scenario];
      const expectedTeams = scenarioConfig?.expectedBracket || scenarioConfig?.expectedTeams || 32;
      await lockTournament(tournamentId, expectedTeams);
      setStepResult('lock', 'success', 'Groups formed');
      
      // Step 4: Verify seeding happened
      setCurrentStep(3);
      setStepResult('seed', 'success', 'Bracket seeded');
      
      // Step 5: Verify bracket
      setCurrentStep(4);
      const bracketResult = await verifyBracket(tournamentId);
      setStepResult('verify_bracket', bracketResult.allPass ? 'success' : 'error', 
        bracketResult.allPass ? 'All checks passed' : 'Some checks failed');
      
      // Wait for lobby generation trigger to complete (runs async after seeding)
      log('Waiting for lobby generation to complete...', 'info');
      await new Promise(r => setTimeout(r, 3000));
      
      // Get forfeit options from scenario
      const forfeitOptions = {
        forfeitChance: scenarioConfig.forfeitChance || 0,
        forfeitType: scenarioConfig.forfeitType || 'single',
      };
      
      // Step 6: Simulate Round 1 (with forfeit options)
      setCurrentStep(5);
      await simulateRound(tournamentId, 1, forfeitOptions);
      setStepResult('simulate', 'success', 'R1 completed');
      
      // Step 7: Verify advancement
      setCurrentStep(6);
      const advanceResult = await verifyAdvancement(tournamentId);
      setStepResult('verify_advance', advanceResult.allPass ? 'success' : 'error',
        advanceResult.allPass ? 'Teams advanced correctly' : 'Advancement issues');
      
      // Step 8: Complete Finals (run all remaining rounds with forfeit options)
      setCurrentStep(7);
      const finalsResult = await completeTournament(tournamentId, forfeitOptions);
      let finalsMessage = finalsResult.success ? 'Champion crowned!' : `Status: ${finalsResult.status}`;
      if (finalsResult.totalForfeits > 0 || finalsResult.totalBracketHoles > 0) {
        finalsMessage += ` (${finalsResult.totalForfeits || 0}F, ${finalsResult.totalBracketHoles || 0}BH)`;
      }
      setStepResult('complete_finals', finalsResult.success ? 'success' : 'error', finalsMessage);
      
      // Step 9: Verify Payouts
      setCurrentStep(8);
      const payoutResult = await verifyPayouts(tournamentId);
      setStepResult('verify_payout', payoutResult.allPass ? 'success' : 'error',
        payoutResult.allPass ? 'Payouts verified!' : 'Payout issues');
      
      log('='.repeat(50), 'info');
      log('FULL E2E TEST COMPLETE', 'success');
      log('='.repeat(50), 'info');
      
    } catch (error) {
      console.error('Test error:', error);
      log(`TEST FAILED: ${error.message}`, 'error');
      
      const currentStepId = steps[currentStep]?.id;
      if (currentStepId) {
        setStepResult(currentStepId, 'error', error.message);
      }
    } finally {
      setLoading(false);
      setIsRunning(false);
    }
  }, [isRunning, clearAll, log, createTournament, addEntries, lockTournament, 
      verifyBracket, simulateRound, verifyAdvancement, completeTournament, verifyPayouts, setStepResult, steps]);
  
  // =========================================================================
  // CLEANUP
  // =========================================================================
  
  const cleanup = useCallback(async () => {
    if (!testTournamentId) return;
    
    Alert.alert(
      'Cleanup Test Data',
      'Delete this test tournament and all associated data?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            setLoading(true);
            log('Cleaning up test data...', 'info');
            
            try {
              const tournamentRef = doc(db, 'tournaments', testTournamentId);
              
              // Delete entries
              const entriesSnap = await getDocs(collection(tournamentRef, 'entries'));
              const batch1 = writeBatch(db);
              entriesSnap.docs.forEach(d => batch1.delete(d.ref));
              if (entriesSnap.size > 0) await batch1.commit();
              
              // Delete matches
              const matchesSnap = await getDocs(collection(tournamentRef, 'matches'));
              const batch2 = writeBatch(db);
              matchesSnap.docs.forEach(d => batch2.delete(d.ref));
              if (matchesSnap.size > 0) await batch2.commit();
              
              // Delete tournament
              await deleteDoc(tournamentRef);
              
              log('Cleanup complete', 'success');
              clearAll();
              
            } catch (error) {
              log(`Cleanup error: ${error.message}`, 'error');
            } finally {
              setLoading(false);
            }
          },
        },
      ]
    );
  }, [testTournamentId, log, clearAll]);
  
  // =========================================================================
  // RENDER
  // =========================================================================
  
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>
          <Ionicons name="flask" size={18} color={COLORS.primary} /> Team Tournament E2E Tester
        </Text>
        <Text style={styles.subtitle}>
          Tests pickup matching, bracket seeding, and team advancement
        </Text>
      </View>
      
      {/* Configuration */}
      {!testTournamentId && (
        <View style={styles.configSection}>
          <Text style={styles.configLabel}>Team Size</Text>
          <View style={styles.configRow}>
            {[2, 3, 4, 5, 6].map(size => (
              <TouchableOpacity
                key={size}
                onPress={() => setTeamSize(size)}
                style={[styles.configBtn, teamSize === size && styles.configBtnActive]}
              >
                <Text style={[styles.configBtnText, teamSize === size && styles.configBtnTextActive]}>
                  {size}v{size}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          
          <Text style={[styles.configLabel, { marginTop: 12 }]}>Test Scenario</Text>
          <View style={styles.scenarioGrid}>
            {Object.entries(SCENARIOS).map(([key, val]) => (
              <TouchableOpacity
                key={key}
                onPress={() => setScenario(key)}
                style={[styles.scenarioBtn, scenario === key && styles.scenarioBtnActive]}
              >
                <Text style={[styles.scenarioBtnText, scenario === key && styles.scenarioBtnTextActive]}>
                  {val.label}
                </Text>
                <Text style={styles.scenarioMeta}>
                  {val.expectedTeams} teams {'->'} {val.expectedBracket} bracket
                  {val.expectedTapered > 0 && ` (${val.expectedTapered} cut)`}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}
      
      {/* Actions */}
      <View style={styles.actions}>
        {!testTournamentId ? (
          <TouchableOpacity
            onPress={runFullTest}
            disabled={loading}
            style={[styles.primaryBtn, loading && styles.btnDisabled]}
          >
            {loading ? (
              <ActivityIndicator color="#000" size="small" />
            ) : (
              <>
                <Ionicons name="play" size={18} color="#000" />
                <Text style={styles.primaryBtnText}>Run Full E2E Test</Text>
              </>
            )}
          </TouchableOpacity>
        ) : (
          <View style={styles.actionRow}>
            <TouchableOpacity
              onPress={runFullTest}
              disabled={loading || isRunning}
              style={[styles.actionBtn, styles.actionBtnPrimary]}
            >
              <Ionicons name="refresh" size={16} color="#000" />
              <Text style={styles.actionBtnTextDark}>Rerun</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={() => verifyBracket(testTournamentId)}
              disabled={loading}
              style={[styles.actionBtn, styles.actionBtnInfo]}
            >
              <Ionicons name="checkmark-done" size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Verify</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={cleanup}
              disabled={loading}
              style={[styles.actionBtn, styles.actionBtnDanger]}
            >
              <Ionicons name="trash" size={16} color="#fff" />
              <Text style={styles.actionBtnText}>Cleanup</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {/* Step Tracker */}
      {currentStep >= 0 && (
        <StepTracker 
          steps={steps} 
          currentStep={currentStep} 
          stepResults={stepResults} 
        />
      )}
      
      {/* Tournament Info */}
      {tournamentData && (
        <View style={styles.infoCard}>
          <Text style={styles.infoTitle}>{tournamentData.name}</Text>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>ID:</Text>
            <Text style={styles.infoValue}>{testTournamentId}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Status:</Text>
            <Text style={[styles.infoValue, styles.infoHighlight]}>{tournamentData.status}</Text>
          </View>
          {tournamentData.bracketSize && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Bracket:</Text>
              <Text style={styles.infoValue}>{tournamentData.bracketSize} teams, {tournamentData.totalRounds} rounds</Text>
            </View>
          )}
        </View>
      )}
      
      {/* Pickup Groups */}
      {pickupGroups.length > 0 && (
        <View style={styles.groupsCard}>
          <Text style={styles.groupsTitle}>Pickup Groups ({pickupGroups.length})</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {pickupGroups.map((group, i) => (
              <View key={group.id} style={[
                styles.groupChip,
                { borderLeftColor: group.type === 'full_team' ? COLORS.success 
                  : group.type === 'partials_combined' ? COLORS.info 
                  : group.type === 'partial_with_solos' ? COLORS.warning 
                  : COLORS.purple }
              ]}>
                <Text style={styles.groupSeed}>#{group.seed || i + 1}</Text>
                <Text style={styles.groupName}>{group.displayName}</Text>
                <Text style={styles.groupType}>P{group.priority} {group.type}</Text>
              </View>
            ))}
          </ScrollView>
        </View>
      )}
      
      {/* Verifications */}
      {verifications.map((v, i) => (
        <VerificationResult key={i} title={v.title} checks={v.checks} />
      ))}
      
      {/* Log Panel */}
      <LogPanel logs={logs} onClear={() => setLogs([])} />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  title: {
    color: COLORS.primary,
    fontSize: 16,
    fontWeight: '800',
  },
  subtitle: {
    color: COLORS.textMuted,
    fontSize: 12,
    marginTop: 4,
  },
  
  // Config
  configSection: {
    marginBottom: 16,
  },
  configLabel: {
    color: COLORS.textMuted,
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  configRow: {
    flexDirection: 'row',
    gap: 8,
  },
  configBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: COLORS.cardAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  configBtnActive: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  configBtnText: {
    color: COLORS.textMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  configBtnTextActive: {
    color: '#000',
  },
  scenarioGrid: {
    gap: 8,
  },
  scenarioBtn: {
    padding: 10,
    borderRadius: 6,
    backgroundColor: COLORS.cardAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  scenarioBtnActive: {
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary + '15',
  },
  scenarioBtnText: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '600',
  },
  scenarioBtnTextActive: {
    color: COLORS.primary,
  },
  scenarioMeta: {
    color: COLORS.textDim,
    fontSize: 10,
    marginTop: 2,
  },
  
  // Actions
  actions: {
    marginBottom: 16,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: COLORS.primary,
    paddingVertical: 14,
    borderRadius: 8,
  },
  primaryBtnText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '700',
  },
  btnDisabled: {
    opacity: 0.5,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 8,
  },
  actionBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 6,
  },
  actionBtnPrimary: {
    backgroundColor: COLORS.primary,
  },
  actionBtnInfo: {
    backgroundColor: COLORS.info,
  },
  actionBtnDanger: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: COLORS.danger,
  },
  actionBtnText: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '600',
  },
  actionBtnTextDark: {
    color: '#000',
    fontSize: 12,
    fontWeight: '600',
  },
  
  // Step Tracker
  stepTracker: {
    marginBottom: 16,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  stepDot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: COLORS.card,
    borderWidth: 2,
    borderColor: COLORS.border,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  stepDotComplete: {
    backgroundColor: COLORS.success,
    borderColor: COLORS.success,
  },
  stepDotFailed: {
    backgroundColor: COLORS.danger,
    borderColor: COLORS.danger,
  },
  stepDotCurrent: {
    borderColor: COLORS.primary,
  },
  stepDotPending: {
    opacity: 0.3,
  },
  stepDotText: {
    color: COLORS.textMuted,
    fontSize: 10,
    fontWeight: '700',
  },
  stepContent: {
    flex: 1,
  },
  stepLabel: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '600',
  },
  stepLabelCurrent: {
    color: COLORS.primary,
  },
  stepLabelPending: {
    color: COLORS.textDim,
  },
  stepMessage: {
    color: COLORS.textMuted,
    fontSize: 10,
    marginTop: 2,
  },
  stepMessageError: {
    color: COLORS.danger,
  },
  stepLine: {
    position: 'absolute',
    left: 10,
    top: 24,
    width: 2,
    height: 20,
    backgroundColor: COLORS.border,
  },
  stepLineComplete: {
    backgroundColor: COLORS.success,
  },
  
  // Info Card
  infoCard: {
    backgroundColor: COLORS.card,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  infoTitle: {
    color: COLORS.text,
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  infoLabel: {
    color: COLORS.textMuted,
    fontSize: 11,
    width: 60,
  },
  infoValue: {
    color: COLORS.text,
    fontSize: 11,
    flex: 1,
  },
  infoHighlight: {
    color: COLORS.primary,
    fontWeight: '600',
  },
  
  // Groups
  groupsCard: {
    marginBottom: 12,
  },
  groupsTitle: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 8,
  },
  groupChip: {
    backgroundColor: COLORS.card,
    borderRadius: 6,
    padding: 8,
    marginRight: 8,
    borderLeftWidth: 3,
    minWidth: 120,
  },
  groupSeed: {
    color: COLORS.primary,
    fontSize: 11,
    fontWeight: '700',
  },
  groupName: {
    color: COLORS.text,
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
  },
  groupType: {
    color: COLORS.textDim,
    fontSize: 9,
    marginTop: 2,
  },
  
  // Verification
  verifyCard: {
    backgroundColor: COLORS.card,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  verifyTitle: {
    color: COLORS.text,
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 8,
  },
  verifyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  verifyText: {
    color: COLORS.textMuted,
    fontSize: 11,
    flex: 1,
  },
  verifyTextFail: {
    color: COLORS.danger,
  },
  
  // Log Panel
  logPanel: {
    backgroundColor: COLORS.cardAlt,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    overflow: 'hidden',
    marginTop: 8,
  },
  logHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
  },
  logTitle: {
    color: COLORS.text,
    fontSize: 11,
    fontWeight: '700',
  },
  logClear: {
    color: COLORS.danger,
    fontSize: 10,
  },
  logScroll: {
    maxHeight: 180,
    padding: 10,
  },
  logLine: {
    fontFamily: 'monospace',
    fontSize: 10,
    marginBottom: 2,
    color: COLORS.textMuted,
  },
  logTime: {
    color: COLORS.textDim,
  },
  logSuccess: {
    color: COLORS.success,
  },
  logWarning: {
    color: COLORS.warning,
  },
  logError: {
    color: COLORS.danger,
  },
  logInfo: {
    color: COLORS.textMuted,
  },
  logPurple: {
    color: COLORS.purple,
  },
  logEmpty: {
    color: COLORS.textDim,
    fontSize: 10,
    fontStyle: 'italic',
  },
});